import { useState, useEffect, useRef, useCallback } from 'react';
import { readShopOccupiedSpotIds } from '@/lib/location/api';
import { availableSpotsFrom, type SpotOption } from '@/lib/location/availableSpots';
import { createTicketBundle } from '@/lib/tickets/createTicketBundle';
import { catalogJobMatchesQuery } from '@/lib/catalog/jobSearch';
import { debugLog } from '@/utils/debugLog';
import { flushSync } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { TopBar } from '@/components/layout/TopBar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { CatalogJobSelects, seedCatalogJobSelection } from '@/components/tickets/CatalogJobSelects';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { fetchAllNewJobCatalogRows, isOfferableCatalogJob, newJobCatalogQuery } from '@/lib/catalog/newJobCatalogQuery';
import { createVehicle, decodeVinOnServer, fetchVehiclesByCustomer } from '@/lib/api/vehicleApi';
import { toast } from '@/hooks/use-toast';
import { Loader2, Plus, X, Car, Wrench, Search, Upload, Pencil, ScanLine, Sparkles } from 'lucide-react';
import { VinScannerDialog } from '@/components/vehicle/VinScannerDialog';
import { useIsDesktop } from '@/hooks/use-mobile';
import { EditSingleJobDialog } from '@/components/tickets/EditSingleJobDialog';
// CAT-6 (K29): the production job picker reads `jobs_catalog` from Supabase
// (see fetchJobs / addJobFromCatalog). The `mockData.jobCategories` stub was
// a dead import — removed so no surface can accidentally bind to the 8-row
// demo taxonomy instead of the real catalog.
import { ROUpload } from '@/components/tickets/ROUpload';
import { RoReviewContext, type RoReviewContextProps } from '@/components/tickets/RoReviewContext';
// Core-100 R4 CORE-Y: which fields an extraction proposed / could not find, kept until submit.
import { ExtractedFieldMark } from '@/components/tickets/ExtractedFieldMark';
import {
  roExtractionProvenance,
  conversationExtractionProvenance,
  creationMethodFor,
  type ExtractionProvenance,
} from '@/lib/intake/extractionProvenance';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { vehiclePackages as allVehiclePackages } from '@/data/vehiclePackages';
import { CustomJobModal } from '@/components/tickets/CustomJobModal';
import { OptiPlaceModal } from '@/components/tickets/OptiPlaceModal';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { logTicketCreated } from '@/utils/ticketEventLogger';
import { useDate } from '@/contexts/DateContext';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { useTutorial } from '@/contexts/TutorialContext';
import { formatInTimeZone } from 'date-fns-tz';
import { shopLocalToUtc } from '@/utils/shopTime';
import {
  isAppointmentDayInShopPast,
  PAST_APPOINTMENT_DATE_MESSAGE,
  PAST_APPOINTMENT_DATE_TITLE,
} from '@/utils/appointmentDatePolicy';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { breadcrumbs } from '@/telemetry/breadcrumbs';
import { clock } from '@/utils/clock';
import { AddonSuggestionModal } from '@/components/tickets/AddonSuggestionModal';
import { evaluateAddonSuggestions } from '@/utils/addonSuggestionRules';
import { adasEvidenceFromDecodedDraft, attachAdasEvidence, loadAdasAdvisoryEvidence } from '@/utils/adasAdvisoryEvidence';
import { parseVehicleExceptions } from '@/utils/addonVehicleApplicability';
import type { JobRelationship } from '@/types/addonSuggestions';
import { recordAddonSuggestionEvent } from '@/utils/addonSuggestionOutcome';
import { acceptedSuggestionCreationJob, type AcceptedCatalogJobRow } from '@/utils/addonAcceptedWork';
import type { AddonSettings, AddonSuggestion, AddonSuggestionType } from '@/types/addonSuggestions';
import { useDemoAutofill } from '@/hooks/useDemoAutofill';
import { fingerprintCreateIntent, getOrMintCreateOperation } from '@/lib/tickets/createTicketOperation';
import { useCatalystAutoAssign } from '@/hooks/useCatalystAutoAssign';
import { useMultiTeam } from '@/hooks/useMultiTeam';
import { useTeams } from '@/hooks/useTeams';
import { TeamSelector } from '@/components/teams/TeamSelector';
import { CustomerSearchBar } from '@/components/customers/CustomerSearchBar';
import { CustomerVehicleSelector } from '@/components/customers/CustomerVehicleSelector';
import type { CustomerSearchResult, CustomerVehicle } from '@/lib/customers/types';
import { StagedPartsSection } from '@/components/tickets/parts-labor/StagedPartsSection';
import { useExposureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags';
import {
  AiIntakeAssistDialog,
  type AiIntakeAssistApplyPayload,
  type AiIntakePendingRecording,
  type ExtractedSuggestedJob,
} from '@/components/tickets/AiIntakeAssistDialog';
import { matchExtractionToCatalog } from '@/lib/aiIntake/applyMatcher';
import { OdometerEntryHint, OptiVehicleProfileCard } from '@/components/vehicle-profile/OptiVehicleProfile';
import type { DecodedFact } from '../../supabase/functions/_shared/vehicleProfile/vpic';
import { formatFactValue } from '../../supabase/functions/_shared/vehicleProfile/facts';
import { composeEngineSize, formatDisplacementLitres } from '../../supabase/functions/_shared/vehicleProfile/engineDisplay';
import { usePermissions } from '@/hooks/usePermissions';
import { buildIntakePrefill } from '@/lib/aiIntake/buildIntakePrefill';
// Core-100 R5 (CORE-AQ): Create Ticket opened from a call's appointment proposal — prefill only; the database confirms
// the proposal against the new ticket in the same transaction (tickets.source_call_proposal_id, 20270206025500).
import {
  FROM_CALL_PROPOSAL_PARAM,
  buildCallProposalPrefill,
  callProposalAvailabilityText,
  callProposalProvenance,
  loadCallProposalForTicket,
  type CallProposalPrefill,
} from '@/lib/callProposal/callProposalTicket';
import { CALL_PROPOSAL_LABEL } from '@/components/tickets/CallAppointmentProposals';
import type { Extraction } from '@/components/tickets/AiIntakeAssistDialog';

const vehicleSchema = z.object({
  year: z.coerce.number().min(1990, 'Year must be 1990 or later').max(clock.now().getFullYear() + 1, 'Invalid year').optional(),
  make: z.string().trim().max(50, 'Make must be less than 50 characters').optional(),
  model: z.string().trim().max(50, 'Model must be less than 50 characters').optional(),
  engineSize: z.string().trim().max(30, 'Engine size must be less than 30 characters').optional(),
  vin: z.string().trim().max(17, 'VIN must be 17 characters or less').optional(),
  licensePlate: z.string().trim().max(15, 'License plate must be less than 15 characters').optional(),
  odometerMiles: z.coerce.number().min(0, 'Odometer must be positive').optional(),
  gvwrLbs: z.coerce.number().min(0, 'GVWR must be positive').optional(),
});

const ticketSchema = z.object({
  vehicle: vehicleSchema,
  customerName: z.string().trim().max(100, 'Name must be less than 100 characters').optional(),
  customerPhone: z.string().trim().max(20, 'Phone must be less than 20 characters').optional(),
  type: z.enum(['Waiter', 'Drop-off']),
  priority: z.enum(['Normal', 'High', 'Emergency']),
  isComeback: z.boolean().default(false),
  isCarryOut: z.boolean().default(false),
  appointmentDate: z.string().min(1, 'Appointment date is required'),
  startTime: z.string().optional(),
  dueOut: z.string().optional(),
}).refine((data) => {
  // If not carry out, require vehicle year, make, and model
  if (!data.isCarryOut) {
    return data.vehicle.year && data.vehicle.make && data.vehicle.model;
  }
  return true;
}, {
  message: "Vehicle year, make, and model are required (unless Carry Out is selected)",
  path: ["vehicle.year"],
});

type TicketFormValues = z.infer<typeof ticketSchema>;

const CreateTicket = () => {
  const navigate = useNavigate();
  const { selectedDate, selectedDayLabel, setSelectedDayLabel } = useDate();
  const { activeShop } = useAuth();
  const { shopTimezone } = useShopTimezone();
  const { currentStepData, nextStep, isActive: tutorialActive } = useTutorial();
  const partsAndLaborEnabled = useExposureFlag('ui_partsAndLabor');
  const aiIntakeEnabled = useFeatureFlag('aiIntakeEnabled');
  const [searchParams] = useSearchParams();
  const isDemoMode = searchParams.get('demo') === 'true';
  
  // Hardcoded demo job data for instant display in demo mode (bypasses state race conditions)
  const demoJob = isDemoMode ? {
    id: 'demo-brake-inspection',
    label: 'Brakes — Inspection / Condition Report',
    description: null,
    category_key: 'brakes',
    sub_menu: 'Inspections',
    minutes_default: 24,
    skill_level: 'basic',
    code: null
  } : null;
  
  // Format date in shop timezone to prevent off-by-one errors
  const formattedDate = formatInTimeZone(selectedDate, shopTimezone, 'yyyy-MM-dd');
  
  const [isLoading, setIsLoading] = useState(false);
  // Phase 4B: each staged job carries an optional client-side `tempJobId` so
  // that staged parts can target a specific staged job. Assigned at the
  // persistence boundary if absent — never relies on insert order.
  const [selectedJobs, setSelectedJobs] = useState<Array<{
    tempJobId?: string;
    category: string;
    submenu: string;
    name: string;
    hours: number;
    bay: string;
    skill: number;
    proficiency_category?: string;
    catalogJobId?: string;
    customHours?: number;
    notes?: string;
  }>>([]);
  // Phase 4B: staged parts (not yet persisted). Inserted into parts_lines AFTER
  // ticket_jobs are persisted, using an explicit tempJobId → realJobId map.
  const [stagedParts, setStagedParts] = useState<import('@/components/tickets/parts-labor/StagedPartsSection').StagedPart[]>([]);
  const [isCustomJobModalOpen, setIsCustomJobModalOpen] = useState(false);
  const [isOptiPlaceModalOpen, setIsOptiPlaceModalOpen] = useState(false);
  const [isAddonModalOpen, setIsAddonModalOpen] = useState(false);
  const [editingJobIndex, setEditingJobIndex] = useState<number | null>(null);
  const [customHoursValue, setCustomHoursValue] = useState('');
  const [optiPlaceData, setOptiPlaceData] = useState<any>(null);
  const [pendingAddonSuggestions, setPendingAddonSuggestions] = useState<AddonSuggestion[]>([]);
  const [addonSettings, setAddonSettings] = useState<AddonSettings | null>(null);
  const [selectedPackageIds, setSelectedPackageIds] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMake, setSelectedMake] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [availableMakes, setAvailableMakes] = useState<string[]>([]);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [availableEngines, setAvailableEngines] = useState<string[]>([]);
  const [isLoadingMakes, setIsLoadingMakes] = useState(false);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [isLoadingEngines, setIsLoadingEngines] = useState(false);
  const [isDecodingVin, setIsDecodingVin] = useState(false);
  const [isPopulatingFromRO, setIsPopulatingFromRO] = useState(false);
  const [isPopulatingFromAiIntake, setIsPopulatingFromAiIntake] = useState(false);
  const [aiIntakeOpen, setAiIntakeOpen] = useState(false);
  // Upload Repair Order — moved from inline section to a modal triggered
  // by the top button cluster (next to AI Intake Assist).
  const [isROUploadOpen, setIsROUploadOpen] = useState(false);
  // What the uploaded RO said that OptiBay did NOT turn into work (ShopMonkey
  // complaints + unsold recommendations). Shown read-only beside the jobs so
  // the advisor can see it without any of it becoming a ticket job.
  const [roReviewContext, setRoReviewContext] = useState<RoReviewContextProps | null>(null);
  // Core-100 R4 CORE-Y: the latest extraction applied to this form (repair order or conversation) — its per-field
  // provenance is shown beside each field, and its origin is recorded on the ticket (creation_method).
  const [extractionProvenance, setExtractionProvenance] = useState<ExtractionProvenance | null>(null);
  // Core-100 R5 (CORE-AQ): the call proposal this Create Ticket was opened from (?fromCallProposal=<id>).
  //   usable      prefilled; the submit carries the proposal (the server confirms it with the ticket)
  //   unavailable not found in this shop / already decided / replaced — nothing filled, no lineage
  //   refused     the server refused the ticket for it (nothing was created) — resubmitting asks again
  //   dropped     the advisor chose to create the ticket without the call suggestion
  const fromCallProposalId = searchParams.get(FROM_CALL_PROPOSAL_PARAM);
  const [callProposal, setCallProposal] = useState<{
    state: 'loading' | 'usable' | 'unavailable' | 'refused' | 'dropped';
    message: string | null;
    prefill: CallProposalPrefill | null;
  } | null>(null);
  const [callConcern, setCallConcern] = useState('');
  const [callProposalJobNames, setCallProposalJobNames] = useState<string[] | null>(null);
  const callProposalLoadedRef = useRef<string | null>(null);
  const callProposalJobsAppliedRef = useRef<string | null>(null);
  // V-OCIS-Intake-CreateTicket-Batch-v1: suggestions that didn't auto-match
  // a catalog row are shown next to the form as a manual-add hint. We never
  // silently insert them into selectedJobs without an explicit advisor click.
  const [aiUnmatchedJobs, setAiUnmatchedJobs] = useState<ExtractedSuggestedJob[]>([]);
  const [aiAdvisorNotes, setAiAdvisorNotes] = useState<string | null>(null);
  /**
   * V-OCIS-Intake-RawAudio-Batch-v1: when the AI Intake Assist modal
   * uploads a raw recording under advisor-attested consent, we hold its
   * artifact metadata here. After a successful ticket-create we POST to
   * `attach-intake-recording-to-ticket` to link the artifact + emit an
   * OCIS `intake_audio_uploaded` event. If the ticket-create flow is
   * abandoned (modal cancel, form reset), the artifact orphans without
   * a ticket — a future retention job will sweep these.
   */
  const [pendingRecording, setPendingRecording] = useState<AiIntakePendingRecording | null>(null);
  const [intakeTranscriptId, setIntakeTranscriptId] = useState<string | null>(null);
  // Stable ref to the latest attach helper. The Catalyst auto-assign hook
  // is initialised before the helper closure is defined, so it reads
  // through this ref to always invoke the freshest closure (which sees
  // current pendingRecording / intakeTranscriptId state).
  const attachPendingRecordingToTicketRef = useRef<(ticketId: string) => Promise<void>>(
    async () => {},
  );
  const [isSelectingExistingVehicle, setIsSelectingExistingVehicle] = useState(false);
  const [customHoursTouched, setCustomHoursTouched] = useState(false);
  const [vinScannerOpen, setVinScannerOpen] = useState(false);
  
  // Desktop detection for VIN scanner button visibility
  const isDesktop = useIsDesktop();
  
  // Catalyst auto-assign hook - bypasses OptiPlace modal for Catalyst/Enterprise tiers
  const { isCatalystShop, isAutoAssigning, performAutoAssign } = useCatalystAutoAssign({
    onFallbackToModal: () => {
      // Fall back to showing OptiPlace modal
      // Note: optiPlaceData is already set before performAutoAssign is called,
      // so we can open the modal unconditionally (avoids stale closure issue)
      setIsOptiPlaceModalOpen(true);
    },
    // V-OCIS-Intake-RawAudio-Batch-v1: attach pending intake recording
    // when Catalyst tier bypasses the OptiPlace modal entirely.
    onCallProposalRefused: (message: string) => setCallProposal((prev) => prev && ({ ...prev, state: 'refused', message })),
    onSuccess: (ticketId: string) => {
      // Fire-and-forget — Catalyst hook navigates on its own; we just
      // need the attach to enqueue. The helper handles its own errors.
      void attachPendingRecordingToTicketRef.current(ticketId);
    },
  });
  
  // Multi-team support
  const { isMultiTeamEnabled } = useMultiTeam();
  const { teams } = useTeams();
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  
  // Customer search state
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerSearchResult | null>(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  // OptiVehicle Profile — a new VIN's decode, shown (not saved) until the vehicle exists;
  // the trusted server records its own decode when vehicle-write creates the vehicle.
  const [draftProfile, setDraftProfile] = useState<{ facts: DecodedFact[]; quality: 'high' | 'partial'; vin?: string } | null>(null);
  const { canEditTickets } = usePermissions();
  const [showVehicleSelector, setShowVehicleSelector] = useState(false);
  
  // Parking spot selector state (Gap 1)
  const [availableParkingSpots, setAvailableParkingSpots] = useState<any[]>([]);
  const [selectedParkingSpotId, setSelectedParkingSpotId] = useState<string>('');
  
  // Auto-select default team when teams load
  useEffect(() => {
    if (isMultiTeamEnabled && teams.length > 0 && !selectedTeamId) {
      const defaultTeam = teams.find(t => t.is_default && t.is_active);
      if (defaultTeam) {
        setSelectedTeamId(defaultTeam.id);
      }
    }
  }, [isMultiTeamEnabled, teams, selectedTeamId]);
  
  // Core-100 R2 CORE-Z: offer only spots that are PHYSICALLY free right now (the move
  // authority's occupancy), re-read whenever the picker opens; a failed read offers none.
  // Supersedes IV-D-29 R157-MED-1's status-list read of tickets.parking_spot_id (as Roadmap #15
  // did for ParkingSpotPickerDialog): physical truth has no status list to drift.
  const refreshParkingSpots = useCallback(async () => {
    if (!activeShop?.id) return;
    const { data: spots, error: spotsError } = await supabase
      .from('parking_lot_spots')
      .select('id, label, spot_type')
      .eq('shop_id', activeShop.id)
      .eq('is_active', true)
      .order('label');
    const normalized: SpotOption[] = (spots || []).map((s: any) => ({
      id: s.id,
      label: s.label,
      spot_type: s.spot_type || 'Standard',
    }));
    const occupied = spotsError
      ? null
      : await readShopOccupiedSpotIds(activeShop.id).catch((e: unknown) => {
          console.warn('[CreateTicket] spot occupancy unavailable; offering no spots:', e);
          return null;
        });
    const available = availableSpotsFrom(normalized, occupied);
    setAvailableParkingSpots(available);
    setSelectedParkingSpotId((cur) => (cur && !available.some((s) => s.id === cur) ? '' : cur));
  }, [activeShop?.id]);

  useEffect(() => {
    void refreshParkingSpots();
  }, [refreshParkingSpots]);
  
  // Job catalog states
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [submenus, setSubmenus] = useState<string[]>([]);
  const [selectedSubmenu, setSelectedSubmenu] = useState<string>('');
  const [jobs, setJobs] = useState<any[]>([]);
  const [selectedJob, setSelectedJob] = useState<any | null>(null);
  const [allJobs, setAllJobs] = useState<any[]>([]);
  const [jobSearchQuery, setJobSearchQuery] = useState('');
  
  // Version counter: incremented on search selection so stale fetches (effects and
  // earlier search selections) bail out instead of overwriting the newer selection
  const searchSelectionVersionRef = useRef(0);

  const form = useForm<TicketFormValues>({
    resolver: zodResolver(ticketSchema),
    defaultValues: {
      vehicle: {
        year: clock.now().getFullYear(),
        make: '',
        model: '',
        engineSize: '',
        vin: '',
        licensePlate: '',
        odometerMiles: undefined,
        gvwrLbs: undefined,
      },
      customerName: '',
      customerPhone: '',
      type: 'Drop-off',
      priority: 'Normal',
      isComeback: false,
      isCarryOut: false,
      // Use the authoritative shop-local dayLabel from DateContext, not a UTC
      // round-trip through selectedDate (noon browser-local → UTC slice can
      // shift day for browsers east of UTC+12).
      appointmentDate: selectedDayLabel,
      startTime: '',
      dueOut: '',
    },
  });

  /**
   * CORE-BP items 3 and 8 — the one place a suggestion outcome is recorded.
   *
   * Three exits from the suggestion modal answer the same question, and the
   * reason this capability under-reported declines in the first place is that
   * each exit built its own payload and they quietly disagreed. They now differ
   * only in the two things that genuinely differ -- which jobs the ticket ended
   * up with, and what the advisor accepted -- and share everything else,
   * including the derivation that makes `accepted + declined` reconcile to
   * `shown`. Omitting `accepted` declines everything shown.
   *
   * Never awaited, by any caller: see `recordAddonSuggestionEvent`.
   */
  const recordAddonOutcome = (
    jobNames: string[],
    accepted?: AddonSuggestionType[],
  ) => {
    if (!activeShop?.id) return;
    // No user id is passed, because CORE-BP item 9 removed the ability of a
    // suggestion outcome to name the person who made it. Authorisation for the
    // write is still the advisor's own session (the INSERT policy admits
    // is_manager_or_writer); the ROW simply no longer says who they were.
    recordAddonSuggestionEvent(supabase, {
      shopId: activeShop.id,
      vehicle: {
        year: form.getValues('vehicle.year'),
        make: form.getValues('vehicle.make'),
        model: form.getValues('vehicle.model'),
      },
      jobNames,
      suggestions: pendingAddonSuggestions,
      accepted,
    });
  };

  // Demo autofill hook for marketing videos
  useDemoAutofill(
    form,
    setSelectedYear,
    setSelectedMake,
    setSelectedModel,
    setJobSearchQuery,
    setCustomHoursValue,
    setCustomHoursTouched
  );

  // Tutorial prefill effect - Step 8 (vehicle/customer)
  useEffect(() => {
    const prefillTutorialData = async () => {
      if (currentStepData?.id !== 'create-ticket-page' || !activeShop) return;

      const tutorialYear = 2022;
      const tutorialMake = 'Honda';
      const tutorialModel = 'Civic';
      const tutorialEngine = '2.0L I4';
      const catalogPackages = ['Asian', 'Domestic', 'Euro', 'EV', 'Diesel', 'Exotic'];

      try {
        // 1) Year
        setSelectedYear(tutorialYear);
        form.setValue('vehicle.year', tutorialYear, { shouldValidate: true });
        await new Promise(r => setTimeout(r, 120));

        // 2) Makes
        const { data: makesData } = await supabase.functions.invoke('get-vehicle-makes-catalog', {
          body: { year: tutorialYear, packageTypes: catalogPackages }
        });
        const makes: string[] = Array.from(new Set([...(makesData?.makes || []), tutorialMake]));
        setAvailableMakes(makes);
        await new Promise(r => setTimeout(r, 60));
        setSelectedMake(tutorialMake);
        form.setValue('vehicle.make', tutorialMake, { shouldValidate: true });
        await form.trigger('vehicle.make');
        await new Promise(r => setTimeout(r, 160));

        // 3) Models
        const { data: modelsData } = await supabase.functions.invoke('get-vehicle-models-catalog', {
          body: { year: tutorialYear, make: tutorialMake, packageTypes: catalogPackages }
        });
        const models: string[] = Array.from(new Set([...(modelsData?.models || []), tutorialModel]));
        setAvailableModels(models);
        await new Promise(r => setTimeout(r, 60));
        setSelectedModel(tutorialModel);
        form.setValue('vehicle.model', tutorialModel, { shouldValidate: true });
        await form.trigger('vehicle.model');
        await new Promise(r => setTimeout(r, 160));

        // 4) Engines
        const { data: enginesData } = await supabase.functions.invoke('get-vehicle-engines-catalog', {
          body: { year: tutorialYear, make: tutorialMake, model: tutorialModel, packageTypes: catalogPackages }
        });
        const engines: string[] = enginesData?.engines?.length ? enginesData.engines : [tutorialEngine];
        setAvailableEngines(engines);
        await new Promise(r => setTimeout(r, 100)); // Wait for engines to load
        const engineToSelect = engines[0] || tutorialEngine;
        form.setValue('vehicle.engineSize', engineToSelect, { shouldValidate: true });
        await form.trigger('vehicle.engineSize');

        // 5) Customer
        form.setValue('customerName', 'Jane Smith', { shouldValidate: true });
        form.setValue('customerPhone', '555-1234', { shouldValidate: true });

        // 6) Final safety: re-assert Make/Model/Engine if any missing
        await new Promise(r => setTimeout(r, 120));
        const vals = form.getValues();
        if (!vals.vehicle.make) {
          form.setValue('vehicle.make', tutorialMake, { shouldValidate: true });
          setSelectedMake(tutorialMake);
          await form.trigger('vehicle.make');
        }
        if (!vals.vehicle.model) {
          // Ensure options include tutorialModel then set
          setAvailableModels((prev) => Array.from(new Set([...(prev || []), tutorialModel])));
          setSelectedModel(tutorialModel);
          form.setValue('vehicle.model', tutorialModel, { shouldValidate: true });
          await form.trigger('vehicle.model');
        }
        if (!vals.vehicle.engineSize) {
          const e = engineToSelect;
          setAvailableEngines((prev) => (prev && prev.length ? prev : engines));
          form.setValue('vehicle.engineSize', e, { shouldValidate: true });
          await form.trigger('vehicle.engineSize');
        }
      } catch (error) {
        console.error('Tutorial prefill error:', error);
      }
    };

    prefillTutorialData();
  }, [currentStepData?.id, activeShop, form]);

  // Tutorial job selection prefill - Step 9
  useEffect(() => {
    const prefillJobSelection = async () => {
      if (currentStepData?.id === 'select-job' && categories.length > 0 && allJobs.length > 0) {
        try {
          // Pick a common category (e.g., 'brakes' or first available)
          const tutorialCategory = categories.includes('brakes') ? 'brakes' : categories[0];
          setSelectedCategory(tutorialCategory);
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Fetch submenus for selected category
          const { data: submenuData } = await newJobCatalogQuery('sub_menu')
            .eq('category_key', tutorialCategory)
            .order('sub_menu');
          
          if (submenuData && submenuData.length > 0) {
            const uniqueSubmenus = [...new Set(submenuData.map(item => item.sub_menu))];
            setSubmenus(uniqueSubmenus);
            const tutorialSubmenu = uniqueSubmenus[0];
            setSelectedSubmenu(tutorialSubmenu);
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Fetch jobs for selected category/submenu
            const { data: catalogJobs } = await newJobCatalogQuery('*')
              .eq('category_key', tutorialCategory)
              .eq('sub_menu', tutorialSubmenu)
              .order('label');
            
            const { data: { user } } = await supabase.auth.getUser();
            let customJobs: any[] = [];
            
            if (user) {
              const { data } = await supabase
                .from('jobs_custom')
                .select('*')
                .eq('user_id', user.id)
                .eq('category_key', tutorialCategory)
                .eq('sub_menu', tutorialSubmenu)
                .order('label');
              
              customJobs = data || [];
            }
            
            const allCategoryJobs = [...(catalogJobs || []), ...customJobs];
            setJobs(allCategoryJobs);
            
            if (allCategoryJobs.length > 0) {
              const tutorialJob = allCategoryJobs[0];
              await new Promise(resolve => setTimeout(resolve, 100));
              setSelectedJob(tutorialJob);
              
              // Populate custom hours and notes for tutorial
              await new Promise(resolve => setTimeout(resolve, 200));
              setCustomHoursValue('1.0');
              setCustomHoursTouched(true);
              
              // Set tutorial note
              const tutorialNote = 'ABS control unit has a core';
              
              // Set notes input value via DOM
              setTimeout(() => {
                const notesInput = document.getElementById('temp-notes') as HTMLInputElement;
                if (notesInput) {
                  notesInput.value = tutorialNote;
                }
              }, 100);
            }
          }
        } catch (error) {
          console.error('Tutorial job prefill error:', error);
        }
      }
    };
    
    prefillJobSelection();
  }, [currentStepData?.id, categories, allJobs]);

  // Update form when calendar date changes
  useEffect(() => {
    form.setValue('appointmentDate', selectedDayLabel);
  }, [selectedDate, form]);

  // Load selected vehicle packages.
  // WS-ELIG B-R3: this used to load the Active roster into state nothing read.
  // A roster read here would be a chooser that bypasses the W3 gate; the
  // assignment on this page goes through OptiPlace / smart-assign, which ask it.
  useEffect(() => {
    const loadData = async () => {
      if (!activeShop) return;

      const { data: profileData } = await supabase
        .from('shop_profile')
        .select('vehicle_packages')
        .eq('shop_id', activeShop.id)
        .single();
      if (profileData?.vehicle_packages && Array.isArray(profileData.vehicle_packages)) {
        setSelectedPackageIds(profileData.vehicle_packages as string[]);
      }

      // Load unique categories
      const { data: catalogData } = await fetchAllNewJobCatalogRows<{ category_key: string }>('category_key', ['category_key']);
      
      if (catalogData) {
        const uniqueCategories = [...new Set(catalogData.map(item => item.category_key))];
        setCategories(uniqueCategories);
      }

      // Load all jobs for search (catalog + custom)
      const { data: allJobsData } = await fetchAllNewJobCatalogRows('*', ['label']);

      const { data: { user } } = await supabase.auth.getUser();
      let customJobsData: any[] = [];
      
      if (user) {
        const { data } = await supabase
          .from('jobs_custom')
          .select('*')
          .eq('user_id', user.id)
          .order('label');
        
        customJobsData = data || [];
      }
      
      if (allJobsData) {
        setAllJobs([...allJobsData, ...customJobsData]);
      }
    };
    loadData();
  }, []);

  // Load addon suggestion settings
  useEffect(() => {
    const loadAddonSettings = async () => {
      if (!activeShop) return;
      
      const { data: settings } = await supabase
        .from('addon_suggestion_settings')
        .select('*')
        .eq('shop_id', activeShop.id)
        .maybeSingle();
      
      if (settings) {
        setAddonSettings({
          alwaysPromptTerms: settings.always_prompt_terms || [],
          neverPromptTerms: settings.never_prompt_terms || [],
          adasYearThreshold: settings.adas_year_threshold || 2016,
          alignmentDefaultChecked: settings.alignment_default_checked ?? true,
          sasDefaultChecked: settings.sas_default_checked ?? true,
          adasDefaultChecked: settings.adas_default_checked ?? false,
          tpmsDefaultChecked: settings.tpms_default_checked ?? false,
          featureEnabled: settings.feature_enabled ?? true,
          precisionFirstMode: settings.precision_first_mode ?? true,
          catalogJobIds: {
            alignment: settings.alignment_catalog_job_id,
            sas: settings.sas_catalog_job_id,
            adas: settings.adas_catalog_job_id,
            tpms: settings.tpms_catalog_job_id,
          },
          // CORE-BP item 2 — the shop's vehicle applicability policy. Creation
          // and the post-add path must read the same policy or the same vehicle
          // would be treated differently depending on when work was added.
          vehicleExceptions: parseVehicleExceptions(
            (settings as { vehicle_exceptions?: unknown }).vehicle_exceptions),
        });
      } else {
        // Set default settings if none exist
        setAddonSettings({
          alwaysPromptTerms: [],
          neverPromptTerms: [],
          adasYearThreshold: 2016,
          alignmentDefaultChecked: true,
          sasDefaultChecked: true,
          adasDefaultChecked: false,
          tpmsDefaultChecked: false,
          featureEnabled: true,
          precisionFirstMode: true,
          catalogJobIds: {},
          vehicleExceptions: [],
        });
      }
    };
    
    loadAddonSettings();
  }, [activeShop]);

  // Fetch submenus when category changes
  useEffect(() => {
    const versionAtStart = searchSelectionVersionRef.current;
    const fetchSubmenus = async () => {
      if (!selectedCategory) {
        setSubmenus([]);
        setSelectedSubmenu('');
        setJobs([]);
        setSelectedJob(null);
        return;
      }

      const { data } = await newJobCatalogQuery('sub_menu')
        .eq('category_key', selectedCategory)
        .order('sub_menu');
      
      // Bail out if a search selection happened while we were fetching
      if (versionAtStart !== searchSelectionVersionRef.current) return;

      if (data) {
        const uniqueSubmenus = [...new Set(data.map(item => item.sub_menu))];
        setSubmenus(uniqueSubmenus);
      }
    };
    fetchSubmenus();
  }, [selectedCategory]);

  // Fetch jobs when submenu changes (including custom jobs)
  useEffect(() => {
    const versionAtStart = searchSelectionVersionRef.current;
    const fetchJobs = async () => {
      if (!selectedCategory || !selectedSubmenu) {
        setJobs([]);
        setSelectedJob(null);
        return;
      }

      // Fetch catalog jobs
      const { data: catalogJobs } = await newJobCatalogQuery('*')
        .eq('category_key', selectedCategory)
        .eq('sub_menu', selectedSubmenu)
        .order('label');

      // Bail out if a search selection happened while we were fetching
      if (versionAtStart !== searchSelectionVersionRef.current) return;

      // Fetch user's custom jobs
      const { data: { user } } = await supabase.auth.getUser();
      let customJobs: any[] = [];
      
      if (user) {
        const { data } = await supabase
          .from('jobs_custom')
          .select('*')
          .eq('user_id', user.id)
          .eq('category_key', selectedCategory)
          .eq('sub_menu', selectedSubmenu)
          .order('label');
        
        customJobs = data || [];
      }
      
      // Bail out if a search selection happened while we were fetching
      if (versionAtStart !== searchSelectionVersionRef.current) return;

      // Combine and set jobs
      setJobs([...(catalogJobs || []), ...customJobs]);
    };
    fetchJobs();
  }, [selectedCategory, selectedSubmenu]);



  const addJobFromCatalog = () => {
    if (!selectedJob) return;
    
    if (!selectedJobs.find(j => j.name === selectedJob.label)) {
      // Map skill_level text to number
      const skillLevelMap: Record<string, number> = {
        'basic': 1,
        'intermediate': 2,
        'advanced': 3,
        'expert': 4,
      };

      const hours = Math.round((selectedJob.minutes_default / 60) * 10) / 10;
      let skillLevel = skillLevelMap[selectedJob.skill_level?.toLowerCase()] || 2;
      
      // Boost diagnostics jobs to expert tier (4)
      if (selectedJob.category_key === 'diagnostics' && skillLevel === 3) {
        skillLevel = 4;
      }
      
      setSelectedJobs([...selectedJobs, {
        tempJobId: crypto.randomUUID(),
        category: selectedJob.category_key,
        submenu: selectedJob.sub_menu,
        name: selectedJob.label,
        hours: hours,
        bay: '', // No specific bay requirement by default
        skill: skillLevel,
        proficiency_category: selectedJob.proficiency_category,
        catalogJobId: selectedJob.id,
        customHours: undefined,
        notes: undefined,
      }]);

      // Reset selections
      setSelectedJob(null);
      setSelectedSubmenu('');
      setSelectedCategory('');
      setSubmenus([]);
      setJobs([]);
    }
  };

  const handleCustomJobCreated = async (job: {
    category: string;
    submenu: string;
    name: string;
    minutes: number;
    bay: string;
    skill: number;
    proficiency_category?: string;
  }) => {
    const hours = Math.round((job.minutes / 60) * 10) / 10;
    setSelectedJobs([...selectedJobs, {
      ...job,
      tempJobId: crypto.randomUUID(),
      catalogJobId: undefined,
      hours: hours,
      customHours: undefined,
      notes: undefined,
    }]);

    // Reload all jobs for search functionality
    const { data: allJobsData } = await fetchAllNewJobCatalogRows('*', ['label']);
    if (allJobsData) {
      setAllJobs(allJobsData);
    }

    // If the new job matches current category/submenu, refresh the dropdown
    if (job.category === selectedCategory && job.submenu === selectedSubmenu) {
      const { data: catalogJobs } = await newJobCatalogQuery('*')
        .eq('category_key', selectedCategory)
        .eq('sub_menu', selectedSubmenu)
        .order('label');

      const { data: { user } } = await supabase.auth.getUser();
      let customJobs: any[] = [];
      
      if (user) {
        const { data } = await supabase
          .from('jobs_custom')
          .select('*')
          .eq('user_id', user.id)
          .eq('category_key', selectedCategory)
          .eq('sub_menu', selectedSubmenu)
          .order('label');
        
        customJobs = data || [];
      }
      
      setJobs([...(catalogJobs || []), ...customJobs]);
    }
  };

  const removeJob = (index: number) => {
    setSelectedJobs(selectedJobs.filter((_, i) => i !== index));
  };

  const updateJobHours = (index: number, hours: string) => {
    const hoursNum = parseFloat(hours);
    if (!isNaN(hoursNum) && hoursNum > 0) {
      const roundedHours = Math.round(hoursNum * 10) / 10;
      const updatedJobs = [...selectedJobs];
      updatedJobs[index] = { ...updatedJobs[index], hours: roundedHours, customHours: roundedHours };
      setSelectedJobs(updatedJobs);
    }
  };

  const updateJobNotes = (index: number, notes: string) => {
    const updatedJobs = [...selectedJobs];
    updatedJobs[index] = { ...updatedJobs[index], notes };
    setSelectedJobs(updatedJobs);
  };

  const formatCategoryDisplay = (key: string): string => {
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim();
  };

  const totalHours = selectedJobs.reduce((sum, job) => sum + job.hours, 0);
  const totalMinutes = Math.round(totalHours * 60); // Convert to minutes for backend
  const predictedMinutes = totalMinutes; // Use actual job minutes - technician efficiency/buffer applied by smart-assign

  // Vehicle data helpers
  const currentYear = clock.now().getFullYear();
  const years = Array.from({ length: currentYear - 1989 }, (_, i) => currentYear + 1 - i);
  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  // Helper to sequentially hydrate vehicle dropdowns from parsed RO data
  const populateVehicleFromParsed = async (
    yearNum: number,
    makeRaw?: string,
    modelRaw?: string
  ) => {
    const normalize = (s: string | undefined | null) => (s ?? '').toString().trim().toLowerCase();

    // Map package IDs to catalog package types (ensure base coverage when packages not selected)
    const packageMapping: Record<string, string> = {
      asian: 'Asian',
      domestic: 'Domestic',
      light_duty_diesel: 'Diesel',
      euro: 'Euro',
      ev: 'EV',
      exotic: 'Exotic',
    };
    const allPackages = ['Asian', 'Domestic', 'Diesel', 'Euro', 'EV', 'Exotic'];
    const catalogPackages =
      selectedPackageIds.length > 0
        ? selectedPackageIds.map((id) => packageMapping[id]).filter(Boolean)
        : allPackages;

    // Step 1: set year so makes/models effects have correct context
    form.setValue('vehicle.year', yearNum);
    setSelectedYear(yearNum);
    await sleep(50);

    if (!makeRaw && !modelRaw) {
      return;
    }

    // Step 2: fetch and select make if we have one
    const { data: makesInvoke } = await supabase.functions.invoke('get-vehicle-makes-catalog', {
      body: { year: yearNum, packageTypes: catalogPackages },
    });
    const makes: string[] = makesInvoke?.makes || [];

    const makeNorm = normalize(makeRaw);
    const matchedMake = makeRaw
      ? makes.find((m) => normalize(m) === makeNorm) || makeRaw
      : '';

    const mergedMakes = Array.from(new Set([...(makes || []), matchedMake].filter(Boolean)));
    setAvailableMakes(mergedMakes);
    await sleep(50);

    if (matchedMake) {
      setSelectedMake(matchedMake);
      form.setValue('vehicle.make', matchedMake, { shouldValidate: true });
      form.clearErrors('vehicle.make');
      await sleep(50);
    }

    // Step 3: fetch and select model if we have one
    if (matchedMake && modelRaw) {
      const { data: modelsInvoke } = await supabase.functions.invoke('get-vehicle-models-catalog', {
        body: { make: matchedMake, year: yearNum, packageTypes: catalogPackages },
      });
      const models: string[] = modelsInvoke?.models || [];
      const modelNorm = normalize(modelRaw);
      const matchedModel =
        models.find((md) => normalize(md) === modelNorm) || modelRaw || '';

      const modelsWithDecoded = Array.from(
        new Set([...(models || []), matchedModel].filter(Boolean))
      );
      setAvailableModels(modelsWithDecoded);
      await sleep(50);

      if (matchedModel) {
        setSelectedModel(matchedModel);
        form.setValue('vehicle.model', matchedModel, { shouldValidate: true });
        form.clearErrors('vehicle.model');
      }
    }
  };
  // Fetch makes when year changes
  useEffect(() => {
    if (isDecodingVin || isPopulatingFromRO || isPopulatingFromAiIntake || isSelectingExistingVehicle) return;
    let stale = false;
    const fetchMakes = async () => {
      if (!selectedYear) {
        setAvailableMakes([]);
        return;
      }

      setIsLoadingMakes(true);
      try {
        // Map package IDs to catalog package types (ensure base coverage during tutorial)
        const packageMapping: Record<string, string> = {
          'asian': 'Asian',
          'domestic': 'Domestic',
          'light_duty_diesel': 'Diesel',
          'euro': 'Euro',
          'ev': 'EV',
          'exotic': 'Exotic',
        };

        const defaultPackages = ['Asian', 'Domestic', 'Euro', 'EV', 'Diesel', 'Exotic'];
        const mapped = selectedPackageIds.map(id => packageMapping[id]).filter(Boolean) as string[];
        const baseList = mapped.length > 0 ? mapped : defaultPackages;
        const catalogPackages = (currentStepData?.id === 'create-ticket-page')
          ? Array.from(new Set<string>([...baseList, ...defaultPackages]))
          : baseList;

        const { data, error } = await supabase.functions.invoke('get-vehicle-makes-catalog', {
          body: {
            year: selectedYear,
            packageTypes: catalogPackages,
          },
        });

        if (stale) return;
        if (error) throw error;
        const makes: string[] = data?.makes || [];
        // Ensure current/prefilled make remains visible in dropdown
        const currentMake = form.getValues('vehicle.make');
        const tutorialMake = 'Honda';
        for (const m of [currentMake, tutorialMake]) {
          if (m && !makes.includes(m)) makes.push(m);
        }
        setAvailableMakes(makes);
      } catch (error) {
        if (stale) return;
        console.error('Error fetching makes:', error);
        breadcrumbs.log('OB:FAIL', { action: 'fetch_makes_failed', error: String(error) });
        toast({
          title: 'Error',
          description: 'Failed to load vehicle makes',
          variant: 'destructive',
        });
        setAvailableMakes([]);
      } finally {
        if (!stale) setIsLoadingMakes(false);
      }
    };

    fetchMakes();
    return () => { stale = true; };
  }, [selectedYear, selectedPackageIds, currentStepData?.id]);

  // Fetch models when make changes
  useEffect(() => {
    if (isDecodingVin || isPopulatingFromRO || isPopulatingFromAiIntake || isSelectingExistingVehicle) return;
    let stale = false;
    const fetchModels = async () => {
      if (!selectedYear || !selectedMake) {
        return;
      }

      setIsLoadingModels(true);
      try {
        // Map package IDs to catalog package types (ensure base coverage during tutorial)
        const packageMapping: Record<string, string> = {
          'asian': 'Asian',
          'domestic': 'Domestic',
          'light_duty_diesel': 'Diesel',
          'euro': 'Euro',
          'ev': 'EV',
          'exotic': 'Exotic',
        };

        const defaultPackages = ['Asian', 'Domestic', 'Euro', 'EV', 'Diesel', 'Exotic'];
        const mapped = selectedPackageIds.map(id => packageMapping[id]).filter(Boolean) as string[];
        const baseList = mapped.length > 0 ? mapped : defaultPackages;
        const catalogPackages = (currentStepData?.id === 'create-ticket-page')
          ? Array.from(new Set<string>([...baseList, ...defaultPackages]))
          : baseList;

        const { data, error } = await supabase.functions.invoke('get-vehicle-models-catalog', {
          body: {
            make: selectedMake,
            year: selectedYear,
            packageTypes: catalogPackages,
          },
        });

        if (stale) return;
        if (error) throw error;
        const models: string[] = data?.models || [];
        // Only ensure current/prefilled model remains visible if it's already in the form
        const currentModel = form.getValues('vehicle.model');
        if (currentModel && !models.includes(currentModel)) {
          models.push(currentModel);
        }
        setAvailableModels(models);
      } catch (error) {
        if (stale) return;
        console.error('Error fetching models:', error);
        breadcrumbs.log('OB:FAIL', { action: 'fetch_models_failed', error: String(error) });
        toast({
          title: 'Error',
          description: 'Failed to load vehicle models',
          variant: 'destructive',
        });
        setAvailableModels([]);
      } finally {
        if (!stale) setIsLoadingModels(false);
      }
    };

    fetchModels();
    return () => { stale = true; };
  }, [selectedYear, selectedMake, selectedPackageIds, currentStepData?.id]);

  // Fetch engines when model changes
  useEffect(() => {
    // During existing-vehicle selection we still want to fetch engine options so the UI
    // can render the currently-selected engine value.
    if (isDecodingVin || isPopulatingFromRO || isPopulatingFromAiIntake || isSelectingExistingVehicle) return;
    
    // Create an abort controller to handle cleanup
    const abortController = new AbortController();
    let isCancelled = false;
    
    const fetchEngines = async () => {
      if (!selectedYear || !selectedMake || !selectedModel) {
        console.log('[fetchEngines] Skipping - missing required data:', { 
          selectedYear, selectedMake, selectedModel, 
          packageCount: selectedPackageIds.length 
        });
        return;
      }

      console.log('[fetchEngines] Starting fetch for:', { 
        make: selectedMake, 
        model: selectedModel, 
        year: selectedYear 
      });
      
      setIsLoadingEngines(true);
      try {
        // Map package IDs to catalog package types (ensure base coverage during tutorial)
        const packageMapping: Record<string, string> = {
          'asian': 'Asian',
          'domestic': 'Domestic',
          'light_duty_diesel': 'Diesel',
          'euro': 'Euro',
          'ev': 'EV',
          'exotic': 'Exotic',
        };

        const defaultPackages = ['Asian', 'Domestic', 'Euro', 'EV', 'Diesel', 'Exotic'];
        const mapped = selectedPackageIds.map(id => packageMapping[id]).filter(Boolean) as string[];
        const baseList = mapped.length > 0 ? mapped : defaultPackages;
        const catalogPackages = (currentStepData?.id === 'create-ticket-page')
          ? Array.from(new Set<string>([...baseList, ...defaultPackages]))
          : baseList;

        console.log('[fetchEngines] Calling edge function with packages:', catalogPackages);

        // Add timeout to prevent hanging
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 15000)
        );
        
        const fetchPromise = supabase.functions.invoke('get-vehicle-engines-catalog', {
          body: {
            make: selectedMake,
            model: selectedModel,
            year: selectedYear,
            packageTypes: catalogPackages,
          },
        });

        const { data, error } = await Promise.race([fetchPromise, timeoutPromise]) as any;

        // Check if we were cancelled while waiting
        if (isCancelled) {
          console.log('[fetchEngines] Request was cancelled');
          return;
        }

        if (error) {
          console.error('[fetchEngines] Edge function returned error:', error);
          throw error;
        }
        
        // Check for application-level error in response
        if (data?.error) {
          console.error('[fetchEngines] Edge function returned app error:', data.error);
          throw new Error(data.error);
        }
        
        const engines: string[] = data?.engines || [];
        console.log('[fetchEngines] Received', engines.length, 'engines:', engines);
        
        // Ensure current/prefilled engine remains visible in dropdown
        const currentEngine = form.getValues('vehicle.engineSize');
        if (currentEngine && !engines.includes(currentEngine)) engines.push(currentEngine);
        setAvailableEngines(engines);
      } catch (error) {
        if (isCancelled) return;
        console.error('[fetchEngines] Error:', error);
        breadcrumbs.log('OB:FAIL', { action: 'fetch_engines_failed', error: String(error) });
        toast({
          title: 'Error',
          description: 'Failed to load engine options',
          variant: 'destructive',
        });
        setAvailableEngines([]);
      } finally {
        if (!isCancelled) {
          setIsLoadingEngines(false);
        }
      }
    };

    fetchEngines();
    
    // Cleanup function
    return () => {
      isCancelled = true;
      abortController.abort();
    };
  }, [selectedYear, selectedMake, selectedModel, selectedPackageIds, currentStepData?.id]);

  // Auto-select first engine when options are loaded and no selection exists
  useEffect(() => {
    // Skip auto-selection when user is selecting an existing vehicle with its own engine value
    if (isSelectingExistingVehicle) return;
    
    const current = form.getValues('vehicle.engineSize');
    if (!isLoadingEngines && selectedModel && availableEngines.length > 0 && !current) {
      const first = availableEngines[0];
      form.setValue('vehicle.engineSize', first, { shouldValidate: true });
      form.trigger('vehicle.engineSize');
    }
  }, [availableEngines, isLoadingEngines, selectedModel, isSelectingExistingVehicle]);

  const decodeVin = async (vin: string) => {
    if (!vin || vin.length !== 17) {
      toast({
        title: 'Invalid VIN',
        description: 'VIN must be exactly 17 characters',
        variant: 'destructive',
      });
      return;
    }

    breadcrumbs.log('OB:GUARD', { action: 'decode_vin' });   // never the VIN itself
    setIsDecodingVin(true);
    
    try {
      // OptiVehicle Profile: the TRUSTED SERVER decodes (one NHTSA request, through
      // the ONE canonical vPIC mapping) and leaves a receipt, so saving this VIN
      // on the vehicle records the facts without asking NHTSA again. The browser
      // never calls NHTSA. legacyBasics is exactly what this form always filled.
      const decoded = activeShop?.id ? await decodeVinOnServer(activeShop.id, vin) : null;
      if (!decoded) {
        setDraftProfile(null);
        toast({ title: 'Not decoded', description: 'NHTSA returned no build data for this VIN. Enter the vehicle details manually.' });
      } else {
        const normalize = (s: string | undefined | null) => (s ?? '').toString().trim().toLowerCase();
        setDraftProfile({ facts: decoded.facts, quality: decoded.quality, vin });
        const yearStr = decoded.legacyBasics.year !== undefined ? String(decoded.legacyBasics.year) : undefined;
        const makeRaw = decoded.legacyBasics.make;
        const modelRaw = decoded.legacyBasics.model;
        const cyl = decoded.legacyBasics.cylinders;
        const displacement = decoded.legacyBasics.displacement;

        // GVWR: never filled from the decoder. The numeric GVWR is the
        // human-confirmed lift-safety value; NHTSA's weight CLASS is shown beside
        // the field as reference only (founder decision).
        
        // Engine text from the ONE formatter: "2.480000" reads "2.5L 5 Cyl". The raw
        // displacement stays in the decode (and the profile facts) untouched.
        const engineSizeGuess = composeEngineSize(displacement, cyl) ?? '';
        // Match the catalog on the figure people read (the catalog lists "2.5L …").
        const displacementShown = formatDisplacementLitres(displacement) ?? displacement;

        // Compute package filter (fallback to all types if shop packages not loaded yet)
        const packageMapping: Record<string, string> = {
          'asian': 'Asian',
          'domestic': 'Domestic',
          'light_duty_diesel': 'Diesel',
          'euro': 'Euro',
          'ev': 'EV',
          'exotic': 'Exotic',
        };
        const allPackages = ['Asian','Domestic','Diesel','Euro','EV','Exotic'];
        const catalogPackages = (selectedPackageIds.length > 0
          ? selectedPackageIds.map(id => packageMapping[id]).filter(Boolean)
          : allPackages);

        // Step 1: Set year and then sequentially fetch + set make → model → engine
        if (yearStr && !isNaN(parseInt(yearStr))) {
          const yearNum = parseInt(yearStr);
          form.setValue('vehicle.year', yearNum);
          setSelectedYear(yearNum);
          await sleep(50);

          // Fetch makes (case-insensitive matching)
          const { data: makesInvoke } = await supabase.functions.invoke('get-vehicle-makes-catalog', {
            body: { year: yearNum, packageTypes: catalogPackages }
          });
          const makes: string[] = makesInvoke?.makes || [];
          const makeNorm = normalize(makeRaw);
          const matchedMake = makes.find(m => normalize(m) === makeNorm) || makeRaw || '';
          // Ensure dropdown has the decoded make before selecting it
          const mergedMakes = Array.from(new Set([...(makes || []), matchedMake].filter(Boolean)));
          setAvailableMakes(mergedMakes);
          await sleep(50);
          if (matchedMake) {
            setSelectedMake(matchedMake);
            form.setValue('vehicle.make', matchedMake, { shouldValidate: true });
            form.clearErrors('vehicle.make');
            await sleep(50);

            // Fetch models for the chosen make
            const { data: modelsInvoke } = await supabase.functions.invoke('get-vehicle-models-catalog', {
              body: { make: matchedMake, year: yearNum, packageTypes: catalogPackages }
            });
            const models: string[] = modelsInvoke?.models || [];
            const modelNorm = normalize(modelRaw);
            const matchedModel = models.find(md => normalize(md) === modelNorm) || modelRaw || '';
            // Ensure dropdown has the decoded model before selecting it
            const modelsWithDecoded = Array.from(new Set([...(models || []), matchedModel].filter(Boolean)));
            setAvailableModels(modelsWithDecoded);
            await sleep(50);
            if (matchedModel) {
              setSelectedModel(matchedModel);
              form.setValue('vehicle.model', matchedModel, { shouldValidate: true });
              form.clearErrors('vehicle.model');
              await sleep(50);

              // Fetch engines for the chosen model
              const { data: enginesInvoke } = await supabase.functions.invoke('get-vehicle-engines-catalog', {
                body: { make: matchedMake, model: matchedModel, year: yearNum, packageTypes: catalogPackages }
              });
              const engines: string[] = enginesInvoke?.engines || [];
              // Include guessed engine if not present
              const enginesWithGuess = Array.from(new Set([...(engines || []), engineSizeGuess].filter(Boolean)));
              setAvailableEngines(enginesWithGuess);
              await sleep(50);

              // Choose best engine
              const dispNorm = normalize(displacementShown);
              const cylNorm = normalize(cyl);
              let bestEngine = enginesWithGuess.find(e => {
                const eNorm = normalize(e);
                return (dispNorm ? eNorm.includes(dispNorm) : true) && (cylNorm ? eNorm.includes(cylNorm) : true);
              });
              if (!bestEngine && displacement) bestEngine = enginesWithGuess.find(e => normalize(e).includes(dispNorm));
              if (!bestEngine && engineSizeGuess) bestEngine = enginesWithGuess.find(e => normalize(e).includes(normalize(engineSizeGuess)));
              if (!bestEngine && displacementShown) bestEngine = enginesWithGuess.find(e => e.toLowerCase().startsWith(displacementShown!.toLowerCase()));
              if (!bestEngine) bestEngine = engineSizeGuess || enginesWithGuess[0];

              if (bestEngine) {
                form.setValue('vehicle.engineSize', bestEngine, { shouldValidate: true });
                form.clearErrors('vehicle.engineSize');
              }
            }
          }
        }

        // Ensure form state updates are flushed
        await form.trigger(['vehicle.year','vehicle.make','vehicle.model','vehicle.engineSize']);

        breadcrumbs.log('OB:ACK', { 
          action: 'vin_decoded', 
          year: yearStr,
          make: makeRaw, 
          model: modelRaw 
        });
        toast({
          title: 'VIN Decoded Successfully',
          description: `${yearStr ?? ''} ${makeRaw ?? ''} ${modelRaw ?? ''}`.trim(),
        });
      }
    } catch (error) {
      console.error('Error decoding VIN:', error);
      breadcrumbs.log('OB:FAIL', { action: 'vin_decode_failed', error: String(error) });
      toast({
        title: 'Error',
        description: 'Failed to decode VIN',
        variant: 'destructive',
      });
    } finally {
      setIsDecodingVin(false);
    }
  };

  // Handler for VIN barcode scanner detection
  const handleVinDetected = async (vin: string) => {
    // Set VIN in form (already normalized/uppercase from scanner)
    form.setValue('vehicle.vin', vin, { shouldValidate: true });
    form.clearErrors('vehicle.vin');
    
    // Close dialog
    setVinScannerOpen(false);
    
    // Trigger existing decode cascade
    await decodeVin(vin);
  };

  // Handler for customer search result selection
  const handleCustomerSearchSelect = (result: CustomerSearchResult) => {
    setSelectedCustomer(result);
    
    // If we matched a vehicle, prefill vehicle info and store vehicle ID
    if (result.vehicle_id) {
      setSelectedVehicleId(result.vehicle_id);
      setShowVehicleSelector(false);
      
      // Prefill vehicle data
      if (result.vehicle_year) {
        form.setValue('vehicle.year', result.vehicle_year);
        setSelectedYear(result.vehicle_year);
      }
      if (result.vehicle_make) {
        form.setValue('vehicle.make', result.vehicle_make);
        setSelectedMake(result.vehicle_make);
      }
      if (result.vehicle_model) {
        form.setValue('vehicle.model', result.vehicle_model);
        setSelectedModel(result.vehicle_model);
      }
      if (result.vin_masked) {
        // VIN is masked from search — user may need to enter full VIN manually
        // but we can pre-fill a partial indicator
      }
      if (result.plate_masked) {
        // Plate is masked from search — same as above
      }
    } else {
      // Customer selected without specific vehicle - show vehicle selector if they have vehicles
      setSelectedVehicleId(null);
      if (result.vehicle_count > 0) {
        setShowVehicleSelector(true);
      } else {
        setShowVehicleSelector(false);
      }
    }
    
    // Prefill customer data
    if (result.customer_name) {
      form.setValue('customerName', result.customer_name);
    }
    // Note: phone is masked in search results — full value available only via customer detail view
  };

  // Handler for vehicle selection from customer's vehicles
  const handleVehicleSelect = async (vehicle: CustomerVehicle) => {
    // Guard prevents make/model/engine fetch effects from racing this selection.
    setIsSelectingExistingVehicle(true);

    setSelectedVehicleId(vehicle.id);
    setShowVehicleSelector(false);

    try {
      const year = vehicle.year ?? null;
      const make = (vehicle.make ?? '').trim();
      const model = (vehicle.model ?? '').trim();
      const engine = (vehicle.engine_size ?? '').trim();

      // Compute package filter (fallback to full catalog until shop packages load)
      const packageMapping: Record<string, string> = {
        asian: 'Asian',
        domestic: 'Domestic',
        light_duty_diesel: 'Diesel',
        euro: 'Euro',
        ev: 'EV',
        exotic: 'Exotic',
      };
      const defaultPackages = ['Asian', 'Domestic', 'Euro', 'EV', 'Diesel', 'Exotic'];
      const mapped = selectedPackageIds.map((id) => packageMapping[id]).filter(Boolean) as string[];
      const catalogPackages = mapped.length > 0 ? mapped : defaultPackages;

      console.log('[handleVehicleSelect] Starting hydration with:', { year, make, model, engine });

      // 1) Year
      if (year) {
        form.setValue('vehicle.year', year, { shouldValidate: true });
        setSelectedYear(year);
        console.log('[handleVehicleSelect] Set year:', year);
      }

      // 2) Make options + make selection - use flushSync to force render before setting form value
      let makes: string[] = [];
      if (year) {
        const { data: makesData } = await supabase.functions.invoke('get-vehicle-makes-catalog', {
          body: { year, packageTypes: catalogPackages },
        });
        makes = Array.from(new Set([...(makesData?.makes || []), make].filter(Boolean)));
        console.log('[handleVehicleSelect] Fetched makes:', makes.length, 'includes vehicle make:', makes.includes(make));
        flushSync(() => {
          setAvailableMakes(makes);
        });
      } else if (make) {
        flushSync(() => {
          setAvailableMakes((prev) => (prev.includes(make) ? prev : [...prev, make]));
        });
      }
      if (make) {
        form.setValue('vehicle.make', make, { shouldValidate: true });
        setSelectedMake(make);
        console.log('[handleVehicleSelect] Set make:', make);
      }

      // 3) Model options + model selection
      let models: string[] = [];
      if (year && make) {
        const { data: modelsData } = await supabase.functions.invoke('get-vehicle-models-catalog', {
          body: { make, year, packageTypes: catalogPackages },
        });
        models = Array.from(new Set([...(modelsData?.models || []), model].filter(Boolean)));
        console.log('[handleVehicleSelect] Fetched models:', models.length, 'includes vehicle model:', models.includes(model));
        flushSync(() => {
          setAvailableModels(models);
        });
      } else if (model) {
        flushSync(() => {
          setAvailableModels((prev) => (prev.includes(model) ? prev : [...prev, model]));
        });
      }
      if (model) {
        form.setValue('vehicle.model', model, { shouldValidate: true });
        setSelectedModel(model);
        console.log('[handleVehicleSelect] Set model:', model);
      }

      // 4) Engine options + engine selection
      let engines: string[] = [];
      if (year && make && model) {
        const { data: enginesData } = await supabase.functions.invoke('get-vehicle-engines-catalog', {
          body: { make, model, year, packageTypes: catalogPackages },
        });
        engines = Array.from(new Set([...(enginesData?.engines || []), engine].filter(Boolean)));
        console.log('[handleVehicleSelect] Fetched engines:', engines.length, 'includes vehicle engine:', engines.includes(engine));
        flushSync(() => {
          setAvailableEngines(engines);
        });
      } else if (engine) {
        flushSync(() => {
          setAvailableEngines((prev) => (prev.includes(engine) ? prev : [...prev, engine]));
        });
      }
      if (engine) {
        form.setValue('vehicle.engineSize', engine, { shouldValidate: true });
        console.log('[handleVehicleSelect] Set engine:', engine);
      }

      // 5) VIN / Plate
      if (vehicle.vin) {
        form.setValue('vehicle.vin', vehicle.vin, { shouldValidate: true });
      }
      if (vehicle.license_plate) {
        form.setValue('vehicle.licensePlate', vehicle.license_plate, { shouldValidate: true });
      }

      await form.trigger(['vehicle.year', 'vehicle.make', 'vehicle.model', 'vehicle.engineSize']);
    } catch (error) {
      console.error('[handleVehicleSelect] Failed to hydrate vehicle dropdowns:', error);
      // Keep already-set values; this just means the dropdown option lists may be partial.
    } finally {
      setIsSelectingExistingVehicle(false);
    }
  };

  // Handler for clearing customer selection
  const handleCustomerClear = () => {
    setSelectedCustomer(null);
    setSelectedVehicleId(null);
    setShowVehicleSelector(false);
  };

  /**
   * V-OCIS-Intake-CreateTicket-Batch-v1: take an AI intake extraction
   * from <AiIntakeAssistDialog> and prefill the Create Ticket form.
   *
   * Mirrors the RO-upload prefill pattern (setIsPopulatingFromRO guard)
   * so the year/make/model/engine effect cascades don't fight the values
   * we're writing. Never calls approve-intake — the existing submit path
   * remains the ticket-creation entrypoint.
   *
   * Job matching: each suggested_job is matched against the loaded
   * jobs_catalog (allJobs) by `code` first, then case-insensitive label.
   * Matched suggestions are pushed into selectedJobs using the same
   * shape addJobFromCatalog produces. Unmatched suggestions are kept
   * in aiUnmatchedJobs so the advisor can manually add them.
   */
  const handleAiIntakeApply = async ({
    extraction,
    transcriptId,
    pendingRecording: incomingRecording,
  }: AiIntakeAssistApplyPayload) => {
    // Stash transcript id + recording metadata for the post-create attach hook.
    setIntakeTranscriptId(transcriptId ?? null);
    setPendingRecording(incomingRecording ?? null);
    setIsPopulatingFromAiIntake(true);
    try {
      // AII-6 (K87-A): customer/vehicle field mapping extracted to the pure,
      // tested `buildIntakePrefill` (trim / VIN-uppercase / finite-mileage gate
      // / waiter-dropoff / promised_time → date). Behavior-preserving: the form
      // applies exactly the fields the extraction carried, nothing defaulted.
      const prefill = buildIntakePrefill(extraction);
      setExtractionProvenance(conversationExtractionProvenance(prefill, extraction.confidence));

      // ---------- Customer ----------
      if (prefill.customerName) {
        form.setValue('customerName', prefill.customerName, { shouldValidate: true });
      }
      if (prefill.customerPhone) {
        form.setValue('customerPhone', prefill.customerPhone, { shouldValidate: true });
      }

      // ---------- Vehicle dropdown cascade ----------
      // Reuse populateVehicleFromParsed so make/model dropdowns hydrate
      // with the AI-suggested values selectable.
      if (prefill.year && (prefill.make || prefill.model)) {
        await populateVehicleFromParsed(prefill.year, prefill.make, prefill.model);
      } else if (prefill.year) {
        form.setValue('vehicle.year', prefill.year, { shouldValidate: true });
        setSelectedYear(prefill.year);
      }

      // ---------- VIN / Plate / Mileage ----------
      if (prefill.vin) {
        form.setValue('vehicle.vin', prefill.vin, { shouldValidate: true });
      }
      if (prefill.licensePlate) {
        form.setValue('vehicle.licensePlate', prefill.licensePlate, { shouldValidate: true });
      }
      if (prefill.odometerMiles != null) {
        form.setValue('vehicle.odometerMiles', prefill.odometerMiles, { shouldValidate: true });
      }

      // ---------- Ticket type ----------
      // Treat explicit is_waiter true as "Waiter"; everything else stays
      // at the user's current value (default 'Drop-off' on a fresh form).
      if (prefill.type) {
        form.setValue('type', prefill.type, { shouldValidate: true });
      }

      // ---------- Promised time ----------
      // ISO timestamp → date portion for appointmentDate. We deliberately
      // skip startTime/dueOut because their meaning depends on Waiter vs
      // Drop-off and the advisor can fill them faster than we can guess.
      if (prefill.appointmentDate) {
        form.setValue('appointmentDate', prefill.appointmentDate, { shouldValidate: true });
      }

      // ---------- Suggested jobs + requested services ----------
      // Pure helper does:
      //   Pass 1: walk extraction.suggested_jobs through 5 tiers
      //           (code → label → catalog_label → alias → synonym map).
      //   Pass 2: walk extraction.requested_services through the same
      //           tiers — Gemini sometimes routes explicit customer
      //           requests like "oil change" there instead of into
      //           suggested_jobs, and we don't want them silently
      //           dropped.
      // Matched rows go to selectedJobs (advisor still reviews + submits
      // normally — no auto-create / auto-schedule). Unmatched stay in
      // the form-side banner for manual review.
      const { matchedAdditions, unmatched } = matchExtractionToCatalog({
        extraction,
        allJobs,
        currentSelectedJobs: selectedJobs,
      });

      if (matchedAdditions.length > 0) {
        setSelectedJobs([...selectedJobs, ...matchedAdditions]);
      }
      setAiUnmatchedJobs(unmatched);

      // ---------- Advisor notes (display-only) ----------
      setAiAdvisorNotes(prefill.advisorNotes);

      toast({
        title: 'AI intake applied',
        description:
          matchedAdditions.length > 0
            ? `Filled customer, vehicle, and added ${matchedAdditions.length} job${matchedAdditions.length === 1 ? '' : 's'}. Review and submit.`
            : 'Form filled. Review the highlighted fields and add jobs manually.',
      });
    } finally {
      setIsPopulatingFromAiIntake(false);
    }
  };

  /**
   * V-OCIS-Intake-RawAudio-Batch-v1: best-effort post-create attach. Calls
   * `attach-intake-recording-to-ticket` once we have a ticket_id; the
   * function is idempotent so retrying with the same args is safe.
   * Failure of this hook does NOT roll back the ticket — the artifact
   * row still exists and a future tool can re-attach if needed.
   */
  // Core-100 R5 (CORE-AQ): open from a call proposal. Read once per shop + proposal; nothing is guessed — a value the
  // call did not carry confidently stays empty with a note (buildCallProposalPrefill). The customer / vehicle are the
  // call's own association, selected through the ordinary selection handlers; nothing is written here.
  useEffect(() => {
    if (!fromCallProposalId || !activeShop?.id) return;
    const key = `${activeShop.id}:${fromCallProposalId}`;
    if (callProposalLoadedRef.current === key) return;
    callProposalLoadedRef.current = key;
    const shopId = activeShop.id;
    setCallProposal({ state: 'loading', message: null, prefill: null });
    void (async () => {
      const loaded = await loadCallProposalForTicket(supabase, shopId, fromCallProposalId);
      if (loaded.availability.kind !== 'usable' || !loaded.proposal) {
        setCallProposal({ state: 'unavailable', message: callProposalAvailabilityText(loaded.availability), prefill: null });
        return;
      }
      // A linked customer this person cannot read is no link at all.
      const call = loaded.call && !loaded.customer
        ? { ...loaded.call, linked_customer_id: null, linked_vehicle_id: null }
        : loaded.call;
      const prefill = buildCallProposalPrefill(loaded.proposal, call, {
        todayInShop: formatInTimeZone(clock.now(), shopTimezone, 'yyyy-MM-dd'),
        maxVehicleYear: clock.now().getFullYear() + 1,
      });
      setCallProposal({ state: 'usable', message: null, prefill });
      setExtractionProvenance(callProposalProvenance(prefill));
      setCallConcern(prefill.concern);
      if (prefill.appointmentDate) setSelectedDayLabel(prefill.appointmentDate);
      if (prefill.customerId && loaded.customer) {
        let vehicles: CustomerVehicle[] = [];
        try {
          vehicles = (await fetchVehiclesByCustomer(shopId, loaded.customer.id)) as unknown as CustomerVehicle[];
        } catch {
          vehicles = [];
        }
        handleCustomerSearchSelect({
          match_type: 'phone', customer_id: loaded.customer.id, vehicle_id: null,
          customer_name: loaded.customer.full_name, phone_masked: null, email_masked: null,
          vehicle_year: null, vehicle_make: null, vehicle_model: null, vin_masked: null, plate_masked: null,
          display_label: loaded.customer.full_name, vehicle_count: vehicles.length,
        });
        const linkedVehicle = prefill.vehicleId ? vehicles.find((v) => v.id === prefill.vehicleId) : undefined;
        if (linkedVehicle) await handleVehicleSelect(linkedVehicle);
      } else if (prefill.vehicle) {
        await populateVehicleFromParsed(prefill.vehicle.year, prefill.vehicle.make, prefill.vehicle.model);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromCallProposalId, activeShop?.id]);

  // ...and the heard service becomes a job ONLY where the canonical catalog matcher resolves it (once, when the
  // catalog is loaded). The matched row carries no note: the concern field holds what was heard.
  useEffect(() => {
    const prefill = callProposal?.state === 'usable' ? callProposal.prefill : null;
    if (!prefill || allJobs.length === 0 || callProposalJobsAppliedRef.current === prefill.proposalId) return;
    callProposalJobsAppliedRef.current = prefill.proposalId;
    if (prefill.requestedServices.length === 0) { setCallProposalJobNames([]); return; }
    const { matchedAdditions } = matchExtractionToCatalog({
      extraction: { suggested_jobs: [], requested_services: prefill.requestedServices } as unknown as Extraction,
      allJobs,
      currentSelectedJobs: selectedJobs,
    });
    setCallProposalJobNames(matchedAdditions.map((j) => j.name));
    if (matchedAdditions.length > 0) {
      setSelectedJobs((prev) => [...prev, ...matchedAdditions.map((j) => ({ ...j, notes: undefined }))]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callProposal, allJobs]);

  // Keep the latest closure available to the Catalyst hook's onSuccess
  // callback (declared above). Runs every render so the ref always
  // points at a closure that reads the current pendingRecording state.
  useEffect(() => {
    attachPendingRecordingToTicketRef.current = (ticketId: string) =>
      attachPendingRecordingToTicket(ticketId);
  });

  const attachPendingRecordingToTicket = async (ticketId: string): Promise<void> => {
    const recording = pendingRecording;
    const transcriptId = intakeTranscriptId;
    if (!recording || !activeShop?.id || !ticketId) return;
    try {
      const { data, error } = await supabase.functions.invoke(
        'attach-intake-recording-to-ticket',
        {
          body: {
            shop_id: activeShop.id,
            ticket_id: ticketId,
            artifact_id: recording.recordingArtifactId,
            transcript_id: transcriptId ?? null,
          },
        },
      );
      if (error) {
        const msg =
          (data as { error?: string } | null)?.error || error.message || 'Attach failed';
        throw new Error(msg);
      }
      const payload = data as { ok?: boolean; error?: string } | null;
      if (!payload || payload.ok === false) {
        throw new Error(payload?.error ?? 'attach-intake-recording-to-ticket returned ok:false');
      }
      // Clear local state regardless of already_attached — the artifact
      // is now bound to this ticket and shouldn't be re-applied to the
      // next ticket the advisor creates.
      setPendingRecording(null);
      setIntakeTranscriptId(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[CreateTicket] attach-intake-recording-to-ticket failed:', msg);
      toast({
        title: 'Recording not attached',
        description: `Ticket was created but the AI intake recording couldn't be attached: ${msg}`,
        variant: 'destructive',
      });
      // Keep pendingRecording so the advisor can try again from a future surface.
    }
  };

  // Handle Create Estimate - creates ticket without scheduling (no tech, bay, date)
  const handleCreateEstimate = async () => {
    const values = form.getValues();
    
    // Validate vehicle info for non-carryout
    if (!values.isCarryOut && (!values.vehicle.year || !values.vehicle.make || !values.vehicle.model)) {
      toast({
        title: 'Vehicle info required',
        description: 'Please fill in year, make, and model',
        variant: 'destructive',
      });
      return;
    }

    if (selectedJobs.length === 0) {
      toast({
        title: 'No jobs selected',
        description: 'Please add at least one job to the estimate',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    
    try {
      if (!activeShop) {
        throw new Error('No active shop');
      }

      // Handle carry-out tickets by providing default vehicle info
      const vehicleInfo = values.isCarryOut ? {
        year: clock.now().getFullYear(),
        make: 'Carry Out',
        model: 'Carry Out',
        engineSize: '',
        vin: '',
        licensePlate: ''
      } : values.vehicle;

      // Create or find vehicle
      let vehicleId = selectedVehicleId;
      
      if (!vehicleId) {
        // Create vehicle via encrypted edge function path
        if (!vehicleId) {
          vehicleId = await createVehicle({
            shop_id: activeShop.id,
            year: vehicleInfo.year,
            make: vehicleInfo.make,
            model: vehicleInfo.model,
            engine_size: vehicleInfo.engineSize || null,
            vin: vehicleInfo.vin || null,
            license_plate: vehicleInfo.licensePlate || null,
            gvwr_lbs: values.vehicle.gvwrLbs || null,
          });
        }
      }

      // Generate ticket ID using UUID for collision safety
      const ticketId = crypto.randomUUID();

      // Create estimate ticket (no scheduling fields)
      // Collect customer complaints from job notes for estimates
      const estimateComplaint = selectedJobs
        .map(job => job.notes)
        .filter(Boolean)
        .join('\n')
        || null;

      // Core-100 R2 CORE-Y: the estimate, its work lines and their links commit together or not
      // at all (create_ticket_bundle, the same atomic primitive Create Ticket uses). Before,
      // each line was a separate browser insert whose error was ignored — and it wrote a
      // CATALOG id (or the category name) into ticket_jobs.job_id, which references the
      // per-ticket `jobs` row, so the lines could never land: an estimate was saved with none.
      await createTicketBundle([{
        ticket: {
          id: ticketId,
          simple_ticket_number: 0, // Trigger auto-sets
          vehicle_id: vehicleId,
          customer_name: values.customerName || null,
          customer_phone: values.customerPhone || null,
          customer_request_notes: estimateComplaint,
          type: values.type,
          priority: values.priority,
          is_comeback: values.isComeback,
          predicted_minutes: predictedMinutes,
          status: 'Active',
          shop_id: activeShop.id,
          is_estimate: true, // THE KEY DIFFERENCE
          creation_method: 'estimate',
          // Scheduling fields explicitly NULL:
          assigned_tech_id: null,
          bay_id: null,
          scheduled_date: null,
          scheduled_start_time: null,
          start_time: clock.now().toISOString(), // Required field - use creation time (DTC-G)
          // Codex: vehicle_present is now derived — no longer written directly
          // Parking spot assignment (Gap 1)
          ...(selectedParkingSpotId ? {
            parking_lot: true,
            parking_spot_id: selectedParkingSpotId
          } : {}),
          // Multi-team support
          ...(isMultiTeamEnabled && selectedTeamId ? { team_id: selectedTeamId } : {}),
        },
        jobs: selectedJobs.map((job) => ({
          name: job.name,
          category: job.category || '',
          submenu: job.submenu || '',
          bay_requirement: job.bay || 'Lift',
          proficiency_category: job.proficiency_category || '',
          skill_level: job.skill,
          standard_minutes: Math.round((job.customHours || job.hours) * 60),
          shop_id: activeShop.id,
        })),
        ticketJobs: selectedJobs.map((job) => ({
          minutes_override: Math.round((job.customHours || job.hours) * 60),
          notes: job.notes || null,
        })),
      }]);

      // OCIS-R70-FIX (HIGH-1): estimate creation previously wrote NO
      // timeline event — estimates appeared in /optiboard with no audit
      // trail of who created them or when. logTicketCreated dual-writes
      // Pensieve (advisor metrics) + OCIS (timeline).
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.id) {
          await logTicketCreated({
            ticketId,
            shopId: activeShop.id,
            userId: user.id,
            ticketType: values.type as 'Waiter' | 'Drop-off',
            totalMinutes: predictedMinutes,
            shopTimezone,
          });
        }
      } catch (logErr) {
        console.error('[CreateTicket] estimate logTicketCreated error:', logErr);
      }

      // V-OCIS-Intake-RawAudio-Batch-v1: attach the AI intake recording
      // (if one is pending) to this new ticket. Best-effort; failure
      // toasts but does not roll back the estimate.
      await attachPendingRecordingToTicket(ticketId);

      toast({
        title: 'Estimate Created',
        description: `Estimate ${ticketId} created. Schedule it via OptiBoard.`,
      });

      navigate('/optiboard?tab=estimates');
    } catch (error: any) {
      console.error('Error creating estimate:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create estimate',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (values: TicketFormValues) => {
    breadcrumbs.log('OB:GUARD', { 
      action: 'create_ticket_start', 
      jobCount: selectedJobs.length,
      ticketType: values.type 
    });
    
    if (selectedJobs.length === 0) {
      breadcrumbs.log('OB:FAIL', { 
        action: 'create_ticket_validation_failed', 
        reason: 'no_jobs_selected' 
      });
      toast({
        title: 'No jobs selected',
        description: 'Please add at least one job to the ticket',
        variant: 'destructive',
      });
      return;
    }

    // Incident 2026-10-05 (Floyd's): a past global day reached OptiPlace and
    // smart-assign refused every start as past. Refuse the day here, in the
    // SHOP's calendar, before OptiPlace opens or anything is called or
    // written — and never move the operator's selected day for them.
    // Applies to every scheduled type (Drop-off, Waiter, Carry Out); Create
    // Estimate schedules nothing and does not pass through here.
    if (isAppointmentDayInShopPast(formattedDate, shopTimezone)) {
      breadcrumbs.log('OB:FAIL', {
        action: 'create_ticket_validation_failed',
        reason: 'appointment_date_in_past',
      });
      toast({
        title: PAST_APPOINTMENT_DATE_TITLE,
        description: PAST_APPOINTMENT_DATE_MESSAGE,
        variant: 'destructive',
      });
      return;
    }

    breadcrumbs.log('OB:COMMIT', {
      action: 'opening_optiplace',
      ticketType: values.type,
      predictedMinutes 
    });

    // Prepare ticket data for OptiPlace - use formattedDate to prevent timezone issues
    const appointmentDateTime = values.type === 'Waiter' && values.startTime
      ? `${formattedDate}T${values.startTime}`
      : `${formattedDate}T08:00:00`;

    // Chronomancer R-WAITER-3: Waiter past-time guard.
    // ImportJobs (the CSV path) clamps past appointment times to "now" at
    // line 449-455 of src/pages/ImportJobs.tsx. The manual-creation path
    // here had no equivalent guard: a user creating a Waiter ticket today
    // who typed a time that has already passed (e.g., 8:30 when it's now
    // 10:15) would get a Waiter scheduled in the past. The server would
    // accept the insert, cascade would anchor to the past time, and the
    // customer-waiting promise would be silently broken before the ticket
    // even reached OptiPlace. Rather than silently clamping (which risks
    // masking an operator typo on a busy board), we fail loudly and let
    // the operator correct the time — matching the Chronomancer "fail
    // loudly when impossible" contract for Waiters.
    if (values.type === 'Waiter' && values.startTime) {
      // Chronomancer R-WAITER-3b: use the canonical shopLocalToUtc helper
      // (src/utils/shopTime.ts:65) rather than hand-rolled Date + format.
      // The previous approach round-tripped through browser-local time,
      // which gave wrong answers for any shop not in the viewer's TZ
      // (a manager in NY verifying an LA shop would see past-time
      // rejections 3h off). shopLocalToUtc is DST-aware and used by
      // every other Waiter write site (ImportJobs, optiqueue-confirm
      // via convertDatetimeLocalToUTC, etc.).
      try {
        const appointmentIso = shopLocalToUtc(formattedDate, values.startTime, shopTimezone);
        const appointmentMs = new Date(appointmentIso).getTime();
        if (Number.isFinite(appointmentMs) && appointmentMs < clock.nowMs()) {
          toast({
            title: 'Waiter time is in the past',
            description:
              'The selected Waiter appointment time has already passed. Pick a time later than now, or choose a future date.',
            variant: 'destructive',
          });
          return;
        }
      } catch (e) {
        // DST gap / invalid time — surface and refuse rather than submit.
        toast({
          title: 'Invalid appointment time',
          description:
            'Could not interpret the selected time in the shop timezone. Please choose a different time.',
          variant: 'destructive',
        });
        return;
      }
    }

    // Auto-populate original_tech_id for comeback tickets
    let originalTechId: string | null = null;
    // A selected vehicle is matched by identity (the row + any same-shop row
    // with the same verified VIN index) — the stored VIN is ciphertext, so
    // comparing it to typed text can never match an encrypted vehicle.
    if (values.isComeback && selectedVehicleId && activeShop) {
      try {
        const { data, error } = await supabase.rpc('get_latest_completed_tech_for_vehicle', {
          p_shop_id: activeShop.id,
          p_vehicle_id: selectedVehicleId,
        });
        if (!error && data) originalTechId = data;
      } catch (e) {
        console.warn('[Comeback] Could not fetch original tech:', e);
      }
    }
    if (!originalTechId && values.isComeback && values.vehicle.vin && activeShop) {
      try {
        // Query for prior completed ticket using RPC to avoid TS type recursion
        const { data, error } = await supabase.rpc('get_latest_completed_tech_by_vin', {
          p_shop_id: activeShop.id,
          p_vin: values.vehicle.vin
        });
        
        if (!error && data) {
          originalTechId = data;
          // R125-L-2: don't log plain VIN — same rule as R31-L-6
          // (no PII in devtools). Log the last-4 only for correlation.
          const vinSuffix = values.vehicle.vin?.slice(-4) ?? '????';
          console.log(`[Comeback] Found original tech ${originalTechId} for VIN ****${vinSuffix}`);
        }
      } catch (e) {
        console.warn('[Comeback] Could not fetch original tech:', e);
      }
    }

    // Handle carry-out tickets by providing default vehicle info
    const vehicleInfo = values.isCarryOut ? {
      year: clock.now().getFullYear(),
      make: 'Carry Out',
      model: 'Carry Out',
      engineSize: '',
      vin: '',
      licensePlate: ''
    } : values.vehicle;

    // Core-100 R5 (CORE-AQ): the ticket carries the call proposal it was created from while the advisor has not
    // dropped it; the database confirms the proposal with the ticket (or refuses both).
    const sourceCallProposalId =
      callProposal?.prefill && (callProposal.state === 'usable' || callProposal.state === 'refused')
        ? callProposal.prefill.proposalId
        : null;

    // Collect customer complaints from all jobs (and the concern from the call, as the advisor left it)
    const customerComplaint = [
      callProposal?.prefill ? callConcern.trim() : '',
      ...selectedJobs.map(job => job.notes),
    ]
      .filter(Boolean)
      .join('\n')
      || undefined;

    const ticketData: any = {
      vehicle: vehicleInfo,
      vehicleId: selectedVehicleId || undefined, // Pass existing vehicle ID if selected
      customerId: selectedCustomer?.customer_id || undefined, // Pass customer ID if selected
      customerName: values.customerName,
      customerPhone: values.customerPhone,
      type: values.type,
      priority: values.priority,
      isComeback: values.isComeback,
      originalTechId,
      appointmentDate: appointmentDateTime,
      dueOut: values.dueOut,
      odometerMiles: values.vehicle.odometerMiles,
      gvwrLbs: values.vehicle.gvwrLbs,
      customerComplaint,
      // Core-100 R4 CORE-Y: how this ticket originated (typed, repair order, conversation) — recorded once.
      creationMethod: creationMethodFor(extractionProvenance),
      // (a ticket created from a call proposal is recorded as 'conversation' by the database itself)
      ...(sourceCallProposalId ? { sourceCallProposalId } : {}),
      jobs: await Promise.all(selectedJobs.map(async (job) => {
        let resolvedCatalogJobId = job.catalogJobId || null;
        
        // Defensive resolver: if catalogJobId is missing, try to resolve by exact name+category match
        if (!resolvedCatalogJobId && job.name && job.category) {
          const { data: match } = await newJobCatalogQuery('id')
            .eq('label', job.name)
            .eq('category_key', job.category)
            .maybeSingle();
          if (match) {
            resolvedCatalogJobId = match.id;
            debugLog('CreateTicket', `Resolved missing catalogJobId for "${job.name}": ${match.id}`);
          } else {
            debugLog('CreateTicket', `No catalog match for "${job.name}" in category "${job.category}"`);
          }
        }
        
        return {
          ...job,
          // Phase 4B: ensure every staged job carries a tempJobId so the
          // downstream parts insert can map tempJobId → real job_id explicitly.
          tempJobId: job.tempJobId ?? crypto.randomUUID(),
          catalog_job_id: resolvedCatalogJobId,
          minutes: Math.round((job.customHours || job.hours) * 60),
        };
      })),
      predictedMinutes,
      startTime: values.startTime, // Pass raw fixed start time for waiter
      // Multi-team support: include team_id if multi-team is enabled
      parkingSpotId: selectedParkingSpotId || null,
      // Phase 4B: staged parts (persisted after ticket_jobs are created).
      stagedParts,
      ...(isMultiTeamEnabled && selectedTeamId ? { team_id: selectedTeamId } : {}),
    };

    // ONE operation identity for this Create Ticket action (createTicketOperation.ts):
    // it survives closing and reopening OptiPlace, so a retry after an ambiguous
    // result resolves to the ticket the first attempt made instead of a second one.
    ticketData.createOperationId = getOrMintCreateOperation(fingerprintCreateIntent({
      customerId: ticketData.customerId ?? null,
      customerName: ticketData.customerName ?? null,
      customerPhone: ticketData.customerPhone ?? null,
      vehicleId: ticketData.vehicleId ?? null,
      vehicle: ticketData.vehicle ?? null,
      type: ticketData.type,
      appointmentDate: ticketData.appointmentDate,
      dueOut: ticketData.dueOut ?? null,
      parkingSpotId: ticketData.parkingSpotId ?? null,
      jobs: (ticketData.jobs ?? []).map((j: any) => [j.catalog_job_id ?? null, j.name ?? null, j.minutes ?? null]),
      ...(sourceCallProposalId ? { sourceCallProposalId } : {}),
    })).id;

    // Check for add-on suggestions before opening OptiPlace modal
    if (addonSettings?.featureEnabled && selectedJobs.length > 0) {
      // CORE-BP Slice A: the evaluator is given the job's catalogue IDENTITY,
      // not just its name. The identity was always here on `selectedJobs` and
      // was being dropped one line before the decision that needed it.
      const evaluatedJobs = selectedJobs.map(job => ({
        catalogJobId: job.catalogJobId ?? null,
        name: job.name,
      }));

      // Real relationships for the catalogue jobs actually on this ticket.
      // Only `followup` edges are requested: `related` is affinity and cannot
      // carry an obligation, and `prerequisite` has no established direction in
      // this schema. Asking the database for only what may be used keeps the
      // refusal visible here as well as in the rules module.
      const catalogIds = evaluatedJobs
        .map(j => j.catalogJobId)
        .filter((id): id is string => !!id);

      let relationships: JobRelationship[] = [];
      if (catalogIds.length > 0) {
        const { data: adjacency, error: adjacencyError } = await supabase
          .from('job_adjacency_map')
          .select('source_job_id, related_job_id, relationship_type')
          .in('source_job_id', catalogIds)
          .eq('relationship_type', 'followup');

        if (adjacencyError) {
          // Suggestions degrade to keyword evidence rather than failing the
          // ticket. A missing relationship read must never cost someone their
          // work — and it must not be silent either.
          breadcrumbs.log('OB:GUARD', {
            action: 'addon_relationships_unavailable',
            reason: adjacencyError.message,
          });
        } else {
          relationships = (adjacency ?? []).map(a => ({
            sourceJobId: a.source_job_id,
            relatedJobId: a.related_job_id,
            relationshipType: a.relationship_type as JobRelationship['relationshipType'],
          }));
        }
      }

      const evaluated = evaluateAddonSuggestions(
        evaluatedJobs,
        {
          year: values.vehicle.year,
          make: values.vehicle.make,
          model: values.vehicle.model,
        },
        addonSettings,
        relationships,
      );

      // OptiVehicle Phase 3A: explanation only — positive ADAS equipment already
      // recorded for this vehicle (or already decoded for this VIN in the draft)
      // may be shown under an EXISTING 'adas' suggestion. No NHTSA call, no
      // re-decode; it never creates, removes or pre-checks a suggestion.
      let adasEvidence = null;
      if (evaluated.some(s => s.type === 'adas')) {
        const vinNow = (values.vehicle.vin || '').trim().toUpperCase();
        adasEvidence = selectedVehicleId
          ? await loadAdasAdvisoryEvidence(selectedVehicleId)
          : draftProfile && draftProfile.vin?.toUpperCase() === vinNow && vinNow
            ? adasEvidenceFromDecodedDraft(draftProfile.facts)
            : null;
      }
      const suggestions = attachAdasEvidence(evaluated, adasEvidence);
      
      if (suggestions.length > 0) {
        // Store ticket data and suggestions, open addon modal
        setOptiPlaceData(ticketData);
        setPendingAddonSuggestions(suggestions);
        setIsAddonModalOpen(true);
        return;
      }
    }
    
    // Store ticket data for potential fallback
    setOptiPlaceData(ticketData);
    
    // CATALYST AUTO-ASSIGN: For Catalyst/Enterprise tiers, bypass OptiPlace modal
    if (isCatalystShop) {
      breadcrumbs.log('OB:COMMIT', { action: 'catalyst_auto_assign_triggered' });
      await performAutoAssign(ticketData);
      return;
    }
    
    // Core tier - proceed to OptiPlace modal
    setIsOptiPlaceModalOpen(true);
  };

  return (
    <div className="relative flex flex-col min-h-screen bg-background">
      <div className="tech-grid" />
      <div className="relative z-10 flex flex-col h-full">
      <TopBar />
      
      <main className="flex-1">
        <div className="max-w-5xl mx-auto p-6">
          <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-3xl font-bold">Create New Ticket</h1>
              <p className="text-muted-foreground mt-1">Add a new service ticket with vehicle and job details</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {aiIntakeEnabled && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setAiIntakeOpen(true)}
                  className="gap-2"
                  data-testid="ai-intake-assist-button"
                >
                  <Sparkles className="h-4 w-4" />
                  AI Intake Assist
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsROUploadOpen(true)}
                className="gap-2"
                data-testid="upload-ro-button"
              >
                <Upload className="h-4 w-4" />
                Upload Repair Order
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/import')}
                className="gap-2"
                data-testid="import-csv-button"
              >
                <Upload className="h-4 w-4" />
                Import CSV
              </Button>
            </div>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit, (errors) => {
              // Handle validation errors
              const errorFields = Object.keys(errors);
              if (errorFields.length > 0) {
                // Get the first error field path
                const firstErrorPath = errorFields[0];
                const firstErrorMessage = errors[firstErrorPath as keyof typeof errors]?.message;
                
                // Map field paths to user-friendly names
                const fieldLabels: Record<string, string> = {
                  'vehicle.year': 'Vehicle Year',
                  'vehicle.make': 'Vehicle Make',
                  'vehicle.model': 'Vehicle Model',
                  'vehicle.engineSize': 'Engine Size',
                  'vehicle.vin': 'VIN',
                  'vehicle.licensePlate': 'License Plate',
                  'customerName': 'Customer Name',
                  'customerPhone': 'Customer Phone',
                  'appointmentDate': 'Appointment Date',
                  'startTime': 'Start Time',
                  'dueOut': 'Due Out Time',
                };
                
                const missingFields = errorFields.map(field => fieldLabels[field] || field).join(', ');
                
                toast({
                  title: 'Missing Required Information',
                  description: `Please fill in: ${missingFields}`,
                  variant: 'destructive',
                });
                
                // Scroll to the first error field
                const firstErrorElement = document.querySelector(`[name="${firstErrorPath}"]`) || 
                                         document.querySelector(`[data-testid="${firstErrorPath}"]`);
                
                if (firstErrorElement) {
                  firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  // Focus the field after scrolling
                  setTimeout(() => {
                    if (firstErrorElement instanceof HTMLElement) {
                      firstErrorElement.focus();
                    }
                  }, 500);
                }
              }
            })} className="space-y-6" autoComplete="off" data-testid="create-ticket-form">
              {/* Customer Search Section */}
              <Card data-testid="customer-search-section">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Find Existing Customer</CardTitle>
                  <CardDescription>
                    Search by name, phone, VIN, or license plate to auto-fill customer and vehicle info
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <CustomerSearchBar
                    onSelectResult={handleCustomerSearchSelect}
                    onClear={handleCustomerClear}
                    selectedResult={selectedCustomer}
                  />
                  
                  {/* Vehicle selector when customer has multiple vehicles */}
                  {showVehicleSelector && selectedCustomer?.customer_id && (
                    <div className="mt-4">
                      <CustomerVehicleSelector
                        customerId={selectedCustomer.customer_id}
                        onSelectVehicle={handleVehicleSelect}
                        onCreateNewVehicle={() => {
                          setShowVehicleSelector(false);
                          // Keep customer, but allow manual vehicle entry
                        }}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Core-100 R5 (CORE-AQ): what the call suggestion filled (and why the rest stayed empty) */}
              {callProposal && (
                <Card className="border-primary/40 bg-primary/5" data-testid="call-proposal-prefill-card" data-state={callProposal.state}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">From a call suggestion</CardTitle>
                    <CardDescription>{CALL_PROPOSAL_LABEL}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    {callProposal.state === 'loading' && <p className="text-muted-foreground">Reading the call suggestion…</p>}
                    {callProposal.message && (
                      <p role="alert" className="font-medium text-destructive" data-testid="call-proposal-message">{callProposal.message}</p>
                    )}
                    {callProposal.prefill && (
                      <>
                        <ul className="list-disc pl-5 space-y-1">
                          {callProposal.prefill.notes.map((n) => (
                            <li key={n.field} data-testid={`call-prefill-${n.field}`} data-filled={String(n.filled)}
                              className={n.filled ? '' : 'text-muted-foreground'}>
                              {n.text}
                            </li>
                          ))}
                          {callProposalJobNames !== null && (
                            <li data-testid="call-prefill-jobs" data-filled={String(callProposalJobNames.length > 0)}
                              className={callProposalJobNames.length > 0 ? '' : 'text-muted-foreground'}>
                              {callProposalJobNames.length > 0
                                ? `Job suggested from the catalog for what was heard: ${callProposalJobNames.join(', ')} — remove it if it is wrong.`
                                : 'No catalog job was matched to what was heard — add the job(s) yourself.'}
                            </li>
                          )}
                        </ul>
                        <Label htmlFor="call-prefill-concern">Concern (from the call — edit as needed)</Label>
                        <Textarea
                          id="call-prefill-concern"
                          data-testid="call-prefill-concern"
                          value={callConcern}
                          onChange={(e) => setCallConcern(e.target.value)}
                          className="min-h-[60px]"
                        />
                        {(callProposal.state === 'usable' || callProposal.state === 'refused') && (
                          <p className="text-muted-foreground" data-testid="call-proposal-commit-note">
                            Submitting creates the ticket and confirms this suggestion on it, in one step. Nothing is sent to the customer.
                          </p>
                        )}
                        {callProposal.state === 'refused' && (
                          <Button type="button" variant="outline" size="sm" data-testid="call-proposal-drop"
                            onClick={() => setCallProposal((prev) => prev && ({ ...prev, state: 'dropped',
                              message: 'Continuing without the call suggestion: the ticket will not be linked to the call.' }))}>
                            Create without the call suggestion
                          </Button>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* AI Intake Assist — unmatched suggestions & advisor notes banner */}
              {(aiUnmatchedJobs.length > 0 || aiAdvisorNotes) && (
                <Card
                  className="border-primary/40 bg-primary/5"
                  data-testid="ai-intake-summary-card"
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Sparkles className="h-5 w-5" />
                      AI Intake Suggestions
                    </CardTitle>
                    <CardDescription>
                      Items the AI surfaced that didn't auto-fill. Add or copy
                      anything you want into the form below.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {aiUnmatchedJobs.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold">Unmatched suggested jobs</p>
                        <ul className="mt-1 space-y-1 text-sm">
                          {aiUnmatchedJobs.map((j, i) => (
                            <li key={i} className="flex flex-wrap items-center gap-2">
                              <span className="font-medium">{j.name}</span>
                              {j.code && <Badge variant="outline">{j.code}</Badge>}
                              {j.estimated_minutes != null && (
                                <Badge variant="outline">{j.estimated_minutes} min</Badge>
                              )}
                              {j.category && <Badge variant="secondary">{j.category}</Badge>}
                              {j.rationale && (
                                <span className="text-xs text-muted-foreground">— {j.rationale}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Use the job picker below to add these manually if they apply.
                        </p>
                      </div>
                    )}
                    {aiAdvisorNotes && (
                      <div>
                        <p className="text-sm font-semibold">Advisor notes</p>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                          {aiAdvisorNotes}
                        </p>
                      </div>
                    )}
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setAiUnmatchedJobs([]);
                          setAiAdvisorNotes(null);
                        }}
                      >
                        Dismiss
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* RO Upload now lives in a modal opened from the top
                  button cluster — see the <Dialog> further below. */}

              {/* All Form Sections Container for Tutorial Spotlight */}
              <div data-testid="ticket-form-sections">
              <div className="grid gap-6 md:grid-cols-2">
                {/* Vehicle Information */}
                <Card data-testid="vehicle-info-section">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Car className="h-5 w-5" />
                      Vehicle Information
                    </CardTitle>
                    <CardDescription>Enter the vehicle details</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <FormField
                      control={form.control}
                      name="vehicle.vin"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>VIN<ExtractedFieldMark provenance={extractionProvenance} field="vehicle.vin" value={field.value} /></FormLabel>
                          <div className="flex gap-2">
                            <FormControl>
                              <Input 
                                {...field} 
                                placeholder="17-character VIN" 
                                maxLength={17}
                                className="uppercase"
                                onChange={(e) => {
                                  const value = e.target.value.toUpperCase();
                                  field.onChange(value);
                                }}
                                data-testid="vehicle-vin-input"
                              />
                            </FormControl>
                            
                            {/* Scan button - mobile/tablet only */}
                            {!isDesktop && (
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => setVinScannerOpen(true)}
                                data-testid="scan-vin-button"
                                className="shrink-0"
                              >
                                <ScanLine className="h-4 w-4" />
                                <span className="hidden sm:inline ml-1">Scan</span>
                              </Button>
                            )}
                            
                            {/* Decode button - always visible */}
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => decodeVin(field.value || '')}
                              disabled={!field.value || field.value.length !== 17 || isDecodingVin}
                              data-testid="decode-vin-button"
                            >
                              {isDecodingVin ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                'Decode'
                              )}
                            </Button>
                          </div>
                          <FormDescription>
                            Enter VIN and click Decode to auto-populate vehicle info
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="vehicle.year"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={isDemoMode ? "text-foreground" : ""}>Year{!isDemoMode && ' *'}<ExtractedFieldMark provenance={extractionProvenance} field="vehicle.year" value={field.value} /></FormLabel>
                          <Select
                            onValueChange={(value) => {
                              const year = parseInt(value);
                              field.onChange(year);
                              setSelectedYear(year);
                              setSelectedMake(null);
                              setSelectedModel(null);
                              form.setValue('vehicle.make', '');
                              form.setValue('vehicle.model', '');
                              form.setValue('vehicle.engineSize', '');
                            }} 
                            value={field.value?.toString()}
                          >
                            <FormControl>
                              <SelectTrigger data-testid="vehicle-year-select">
                                <SelectValue placeholder="Select year" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {years.filter(year => year).map(year => (
                                <SelectItem key={year} value={year.toString()} data-testid={`vehicle-year-option-${year}`}>
                                  {year}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {!isDemoMode && <FormMessage />}
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="vehicle.make"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={isDemoMode ? "text-foreground" : ""}>Make{!isDemoMode && ' *'}<ExtractedFieldMark provenance={extractionProvenance} field="vehicle.make" value={field.value} /></FormLabel>
                          <Select
                            onValueChange={(value) => {
                              field.onChange(value);
                              setSelectedMake(value);
                              setSelectedModel(null);
                              form.setValue('vehicle.model', '');
                              form.setValue('vehicle.engineSize', '');
                            }} 
                            value={field.value}
                            disabled={!selectedYear}
                          >
                            <FormControl>
                              <SelectTrigger data-testid="vehicle-make-select">
                                <SelectValue placeholder="Select make" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {isLoadingMakes ? (
                                <div className="p-2 text-center text-sm text-muted-foreground">Loading makes...</div>
                              ) : availableMakes.length === 0 ? (
                                <div className="p-2 text-center text-sm text-muted-foreground">No makes available</div>
                              ) : (
                                availableMakes.filter(make => make && make.trim() !== '').map(make => (
                                  <SelectItem key={make} value={make} data-testid={`vehicle-make-option-${make}`}>
                                    {make}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                          {!isDemoMode && <FormMessage />}
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="vehicle.model"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={isDemoMode ? "text-foreground" : ""}>Model{!isDemoMode && ' *'}<ExtractedFieldMark provenance={extractionProvenance} field="vehicle.model" value={field.value} /></FormLabel>
                          <Select
                            onValueChange={(value) => {
                              field.onChange(value);
                              setSelectedModel(value);
                              form.setValue('vehicle.engineSize', '');
                            }} 
                            value={field.value}
                            disabled={!selectedMake}
                          >
                            <FormControl>
                              <SelectTrigger data-testid="vehicle-model-select">
                                <SelectValue placeholder="Select model" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {isLoadingModels ? (
                                <div className="p-2 text-center text-sm text-muted-foreground">Loading models...</div>
                              ) : availableModels.length === 0 ? (
                                <div className="p-2 text-center text-sm text-muted-foreground">No models available</div>
                              ) : (
                                availableModels.filter(model => model && model.trim() !== '').map(model => (
                                  <SelectItem key={model} value={model} data-testid={`vehicle-model-option-${model}`}>
                                    {model}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                          {!isDemoMode && <FormMessage />}
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="vehicle.engineSize"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Engine Size<ExtractedFieldMark provenance={extractionProvenance} field="vehicle.engineSize" value={field.value} /></FormLabel>
                          <Select 
                            onValueChange={field.onChange} 
                            value={field.value}
                            disabled={!selectedModel}
                          >
                            <FormControl>
                              <SelectTrigger data-testid="vehicle-engine-select">
                                <SelectValue placeholder="Select engine size" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {isLoadingEngines ? (
                                <div className="p-2 text-center text-sm text-muted-foreground">Loading engines...</div>
                              ) : availableEngines.length === 0 ? (
                                <div className="p-2 text-center text-sm text-muted-foreground">No engines available</div>
                              ) : (
                                availableEngines.filter(engine => engine && engine.trim() !== '').map(engine => (
                                  <SelectItem key={engine} value={engine} data-testid={`vehicle-engine-option-${engine}`}>
                                    {engine}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="vehicle.licensePlate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>License Plate<ExtractedFieldMark provenance={extractionProvenance} field="vehicle.licensePlate" value={field.value} /></FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="ABC123" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="vehicle.odometerMiles"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Odometer (miles)<ExtractedFieldMark provenance={extractionProvenance} field="vehicle.odometerMiles" value={field.value} /></FormLabel>
                          <FormControl>
                            <Input 
                              {...field} 
                              type="number" 
                              placeholder="50000"
                              value={field.value ?? ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                field.onChange(val === '' ? undefined : parseInt(val, 10));
                              }}
                            />
                          </FormControl>
                          {/* OptiVehicle: last recorded reading for a returning vehicle (never blocks) */}
                          <OdometerEntryHint vehicleId={selectedVehicleId} entered={field.value} />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="vehicle.gvwrLbs"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>GVWR (lbs)</FormLabel>
                          <FormControl>
                            <Input 
                              {...field} 
                              type="number" 
                              placeholder="e.g. 6000"
                              value={field.value ?? ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                field.onChange(val === '' ? undefined : parseInt(val, 10));
                              }}
                            />
                          </FormControl>
                          <FormDescription className="text-xs">
                            Lift safety uses this number. Enter it from the door-jamb label — NHTSA reports only a weight class (shown in the vehicle profile).
                          </FormDescription>
                          {(() => {
                            // Reference only: the decoded NHTSA weight CLASS is never this vehicle's
                            // confirmed GVWR and nothing here copies it into the field.
                            const cls = draftProfile?.facts.find((f) => f.key === 'gvwr_class')?.value;
                            return cls ? (
                              <p className="text-xs text-muted-foreground" data-testid="gvwr-class-reference">
                                NHTSA GVWR class (reference only): {formatFactValue('gvwr_class', cls)}
                              </p>
                            ) : null;
                          })()}
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    {/* OptiVehicle Profile — the same component and data the Ticket Command Center uses */}
                    {(() => {
                      const vinNow = (form.watch('vehicle.vin') || '').toUpperCase();
                      const draft = !selectedVehicleId && draftProfile && draftProfile.facts.length > 0 && draftProfile.vin?.toUpperCase() === vinNow
                        ? {
                            facts: draftProfile.facts, quality: draftProfile.quality,
                            basics: { year: form.watch('vehicle.year') ?? null, make: form.watch('vehicle.make') || null, model: form.watch('vehicle.model') || null,
                              engine_size: form.watch('vehicle.engineSize') || null, gvwr_lbs: form.watch('vehicle.gvwrLbs') ?? null },
                          }
                        : null;
                      if (!selectedVehicleId && !draft) return null;
                      return (
                        <OptiVehicleProfileCard
                          shopId={activeShop?.id ?? ''}
                          vehicleId={selectedVehicleId}
                          draft={draft}
                          vin={vinNow || null}
                          plate={form.watch('vehicle.licensePlate') || null}
                          canEdit={canEditTickets}
                          context="create"
                        />
                      );
                    })()}

                    {/* Carry Out Checkbox */}
                    <FormField
                      control={form.control}
                      name="isCarryOut"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 bg-muted/50">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="cursor-pointer">
                              Carry Out
                            </FormLabel>
                            <FormDescription>
                              Check this for carry-out parts or services without a specific vehicle
                            </FormDescription>
                          </div>
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>

                {/* Customer & Ticket Info */}
                <Card data-testid="customer-info-section">
                  <CardHeader>
                    <CardTitle>Customer & Ticket Details</CardTitle>
                    <CardDescription>Configure ticket settings</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <FormField
                      control={form.control}
                      name="customerName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Customer Name<ExtractedFieldMark provenance={extractionProvenance} field="customerName" value={field.value} /></FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="John Smith" autoComplete="off" data-testid="customer-name-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="customerPhone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Customer Phone<ExtractedFieldMark provenance={extractionProvenance} field="customerPhone" value={field.value} /></FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="555-0100" autoComplete="off" data-testid="customer-phone-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Ticket Type *</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="ticket-type-select">
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Waiter">Waiter</SelectItem>
                              <SelectItem value="Drop-off">Drop-off</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="priority"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Priority *</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="ticket-priority-select">
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Normal">Normal</SelectItem>
                              <SelectItem value="High">High</SelectItem>
                              <SelectItem value="Emergency">Emergency</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {/* Team selector - only visible for multi-team shops */}
                    {isMultiTeamEnabled && (
                      <FormItem>
                        <FormLabel>Team</FormLabel>
                        <TeamSelector
                          value={selectedTeamId}
                          onValueChange={setSelectedTeamId}
                        />
                        <FormDescription>
                          Assign this ticket to a specific team
                        </FormDescription>
                      </FormItem>
                    )}
                    <FormField
                      control={form.control}
                      name="appointmentDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Appointment Date *</FormLabel>
                          <FormControl>
                            <Input
                              type="date"
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                // The ticket is created on the selected shop day: a day typed here IS that day
                                // (before, the typed value was shown but the submit used the board's day).
                                if (/^\d{4}-\d{2}-\d{2}$/.test(e.target.value)) setSelectedDayLabel(e.target.value);
                              }}
                              data-testid="appointment-date-input"
                            />
                          </FormControl>
                          <FormDescription>
                            {form.watch('type') === 'Drop-off' 
                              ? 'Date customer wants to schedule appointment'
                              : 'Date of appointment'}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {form.watch('type') === 'Waiter' && (
                      <FormField
                        control={form.control}
                        name="startTime"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Start Time</FormLabel>
                            <FormControl>
                              <Input type="time" {...field} data-testid="start-time-input" />
                            </FormControl>
                            <FormDescription>
                              Specific time customer is waiting (optional)
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                    <FormField
                      control={form.control}
                      name="dueOut"
                      render={({ field }) => (
                        <FormItem>
                          {/* Core-100 CORE-BG (direction 2): for a waiter, due_out IS the promised ready
                              time — distinct from the start time above, and what OptiBay tracks. */}
                          <FormLabel>{form.watch('type') === 'Waiter' ? 'Promised Ready Time' : 'Due Out Time'}</FormLabel>
                          <FormControl>
                            <Input type="datetime-local" {...field} data-testid="due-out-input" />
                          </FormControl>
                          <FormDescription>
                            {form.watch('type') === 'Waiter'
                              ? 'When the waiting customer was told the vehicle will be ready (tracked against the remaining work)'
                              : 'When customer needs vehicle back'}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="isComeback"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel>Comeback Ticket 🚨</FormLabel>
                            <FormDescription>
                              Check if this is a comeback/warranty ticket
                            </FormDescription>
                          </div>
                        </FormItem>
                      )}
                    />
                    
                    {/* Parking Spot Assignment */}
                    {availableParkingSpots.length > 0 && (
                      <FormItem>
                        <FormLabel>Parking Spot</FormLabel>
                        <Select
                          value={selectedParkingSpotId || 'none'}
                          onValueChange={(v) => setSelectedParkingSpotId(v === 'none' ? '' : v)}
                          onOpenChange={(open) => { if (open) void refreshParkingSpots(); }}
                        >
                          <SelectTrigger data-testid="parking-spot-select">
                            <SelectValue placeholder="Not assigned" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Not assigned</SelectItem>
                            {availableParkingSpots.map(spot => (
                              <SelectItem key={spot.id} value={spot.id}>
                                {spot.label} — {spot.spot_type || 'Standard'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Assign vehicle to a specific parking spot at check-in
                        </FormDescription>
                      </FormItem>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div> {/* end ticket-form-sections */}

              {/* Job Selection */}
              <Card data-testid="jobs-selection-section">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Wrench className="h-5 w-5" />
                    Select Jobs
                  </CardTitle>
                  <CardDescription>Choose services to perform</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex gap-2 mb-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsCustomJobModalOpen(true)}
                        className="flex-1"
                        data-testid="custom-job-button"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Custom Job
                      </Button>
                    </div>

                    {/* Fuzzy Search Job Selection */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Quick Search</label>
                      <Command shouldFilter={false} className="rounded-lg border">
                        <CommandInput 
                          placeholder="Search jobs..." 
                          value={jobSearchQuery}
                          onValueChange={setJobSearchQuery}
                          data-testid="job-search-input"
                        />
                        {jobSearchQuery.length > 0 && (
                          <CommandList>
                            <CommandEmpty>No jobs found.</CommandEmpty>
                            <CommandGroup>
                              {allJobs
                                .filter((job) => catalogJobMatchesQuery(job, jobSearchQuery))
                                .slice(0, 15)
                                .map((job) => (
                                  <CommandItem
                                    key={job.id}
                                    data-testid={`job-result-${job.id}`}
                                    value={`${job.label} ${job.category_key} ${job.sub_menu}`}
                                    onSelect={() => {
                                      // In-flight list fetches for an earlier selection bail out.
                                      searchSelectionVersionRef.current += 1;
                                      setJobSearchQuery('');
                                      // The result IS the catalog row: commit category + submenu + job
                                      // at once, with lists that already contain them. The category /
                                      // submenu effects then load the full option lists.
                                      const next = seedCatalogJobSelection(job, {
                                        category: selectedCategory,
                                        submenu: selectedSubmenu,
                                        submenus,
                                        jobs,
                                      });
                                      setSelectedCategory(next.category);
                                      setSubmenus(next.submenus);
                                      setSelectedSubmenu(next.submenu);
                                      setJobs(next.jobs);
                                      setSelectedJob(next.job);
                                    }}
                                  >
                                    <div className="flex flex-col">
                                      <span className="font-medium">{job.label}</span>
                                      <span className="text-xs text-muted-foreground">
                                        {formatCategoryDisplay(job.category_key)} → {job.sub_menu} ({job.minutes_default} min)
                                      </span>
                                    </div>
                                  </CommandItem>
                                ))}
                            </CommandGroup>
                          </CommandList>
                        )}
                      </Command>
                    </div>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">Or browse by category</span>
                      </div>
                    </div>

                    {/* Category / Submenu / Job Dropdowns */}
                    <CatalogJobSelects
                      categories={categories}
                      submenus={submenus}
                      jobs={jobs}
                      category={selectedCategory}
                      submenu={selectedSubmenu}
                      job={selectedJob}
                      onCategoryChange={setSelectedCategory}
                      onSubmenuChange={setSelectedSubmenu}
                      onJobChange={setSelectedJob}
                      formatCategory={formatCategoryDisplay}
                    />

                    {(() => {
                      const displayJob = selectedJob || demoJob;
                      return displayJob && (
                        <div className="border rounded-lg p-4 space-y-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium">{displayJob.label}</h4>
                              {displayJob.description && (
                                <p className="text-sm text-muted-foreground mt-1">{displayJob.description}</p>
                              )}
                              <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                                <span>{displayJob.minutes_default} min</span>
                                <span>Skill: {displayJob.skill_level}</span>
                                {displayJob.code && <span>Code: {displayJob.code}</span>}
                              </div>
                            </div>
                          </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label htmlFor="temp-hours" className="text-xs">
                              Custom Hours
                            </Label>
                            <Input
                              id="temp-hours"
                              type="number"
                              step="0.5"
                              min="0"
                              placeholder={`Default: ${(displayJob.minutes_default / 60).toFixed(1)} hrs`}
                              className={`h-9 ${!customHoursTouched && customHoursValue === "" ? "border-red-500 focus:border-red-500" : ""}`}
                              value={customHoursValue}
                              onChange={(e) => {
                                setCustomHoursValue(e.target.value);
                                setCustomHoursTouched(true);
                              }}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="temp-notes" className="text-xs">
                              Customer Complaint
                            </Label>
                            <Input
                              id="temp-notes"
                              placeholder="Describe customer complaint..."
                              className="h-9"
                            />
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              if (!isDemoMode) {
                                setSelectedJob(null);
                                setCustomHoursValue('');
                              }
                            }}
                            disabled={isDemoMode}
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            data-testid="add-job-button"
                            onClick={() => {
                              const hoursInput = document.getElementById('temp-hours') as HTMLInputElement;
                              const notesInput = document.getElementById('temp-notes') as HTMLInputElement;
                              
                              const customHours = hoursInput?.value ? parseFloat(hoursInput.value) : undefined;
                              const notes = notesInput?.value || undefined;
                              
                              // Reset custom hours touched state
                              setCustomHoursTouched(false);
                              
                              const jobToAdd = selectedJob || demoJob;
                              if (jobToAdd && !selectedJobs.find(j => j.name === jobToAdd.label)) {
                                const skillLevelMap: Record<string, number> = {
                                  'basic': 1,
                                  'intermediate': 2,
                                  'advanced': 3,
                                  'expert': 4,
                                };

                                const hours = customHours 
                                  ? Math.round(customHours * 10) / 10
                                  : Math.round((jobToAdd.minutes_default / 60) * 10) / 10;

                                let skillLevel = skillLevelMap[jobToAdd.skill_level?.toLowerCase()] || 2;
                                
                                // Boost diagnostics jobs to expert tier (4)
                                if (jobToAdd.category_key === 'diagnostics' && skillLevel === 3) {
                                  skillLevel = 4;
                                }

                                setSelectedJobs([...selectedJobs, {
                                  tempJobId: crypto.randomUUID(),
                                  category: jobToAdd.category_key,
                                  submenu: jobToAdd.sub_menu,
                                  name: jobToAdd.label,
                                  hours: hours,
                                  bay: '', // No specific bay requirement by default
                                  skill: skillLevel,
                                  customHours: customHours,
                                  notes: notes,
                                }]);

                                // Reset
                                setSelectedJob(null);
                                setSelectedSubmenu('');
                                setSelectedCategory('');
                                setSubmenus([]);
                                setJobs([]);
                                setCustomHoursValue('');
                                
                                // Tutorial: Auto-advance to step 10 when job is added on step 9
                                if (currentStepData?.id === 'select-job') {
                                  setTimeout(() => {
                                    nextStep();
                                  }, 500);
                                }
                              }
                            }}
                            disabled={
                              selectedJobs.some(j => j.name === displayJob.label) || 
                              !customHoursValue
                            }
                            className="flex-1"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Job
                          </Button>
                        </div>
                        </div>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>

              {/* What the RO said that did NOT become work */}
              {roReviewContext && <RoReviewContext {...roReviewContext} />}

              {/* Current Jobs */}
              <div data-testid="current-jobs-section">
                {selectedJobs.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Wrench className="h-5 w-5" />
                        Current Jobs
                      </CardTitle>
                      <CardDescription>Jobs added to this ticket</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3" data-testid="current-jobs-list">
                        {selectedJobs.map((job, idx) => (
                          <div key={idx} className="border rounded-lg p-4 space-y-3">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h5 className="font-medium">{job.name}</h5>
                                <p className="text-sm text-muted-foreground">
                                  {formatCategoryDisplay(job.category)} → {job.submenu}
                                </p>
                              </div>
                              <Badge variant="secondary">{job.hours.toFixed(1)} hrs</Badge>
                            </div>

                            {(job.customHours || job.notes) && (
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                {job.customHours && (
                                  <div>
                                    <span className="text-muted-foreground">Custom Hours: </span>
                                    <span className="font-medium">{job.customHours} hrs</span>
                                  </div>
                                )}
                                {job.notes && (
                                  <div className="col-span-2">
                                    <span className="text-muted-foreground">Complaint: </span>
                                    <span>{job.notes}</span>
                                  </div>
                                )}
                              </div>
                            )}

                            <div className="flex gap-2">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingJobIndex(idx)}
                              >
                                <Pencil className="h-4 w-4 mr-1" />
                                Edit
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => removeJob(idx)}
                              >
                                <X className="h-4 w-4 mr-1" />
                                Remove
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-sm text-muted-foreground mt-4">
                        Total Time: {totalHours.toFixed(1)} hrs ({totalMinutes} min)
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Phase 4B: staged parts (gated by ui_partsAndLabor) */}
                {partsAndLaborEnabled && (
                  <StagedPartsSection
                    stagedParts={stagedParts}
                    onChange={setStagedParts}
                    jobOptions={selectedJobs
                      .filter((j) => !!j.tempJobId)
                      .map((j) => ({ tempJobId: j.tempJobId!, name: j.name }))}
                  />
                )}

                {/* Submit */}
                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/')}
                    disabled={isLoading}
                    data-testid="create-ticket-cancel-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCreateEstimate}
                    disabled={isLoading || isAutoAssigning}
                    data-testid="create-estimate-btn"
                    className="border-dashed"
                  >
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Create Estimate
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={isLoading || isAutoAssigning} 
                    data-testid="create-ticket-submit-btn"
                    className="border-2 border-primary"
                    onClick={() => {
                      // Auto-advance tutorial from step 10 to step 11 when create ticket is pressed
                      if (tutorialActive && currentStepData?.id === 'review-current-jobs') {
                        setTimeout(() => nextStep(), 100);
                      }
                    }}
                  >
                    {(isLoading || isAutoAssigning) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {isAutoAssigning ? 'Scheduling...' : 'Create Ticket'}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </div>
      </main>

      <CustomJobModal
        isOpen={isCustomJobModalOpen}
        onClose={() => setIsCustomJobModalOpen(false)}
        shopId={activeShop?.id || ''}
        onJobCreated={handleCustomJobCreated}
      />
      
      {optiPlaceData && pendingAddonSuggestions.length > 0 && (
        <AddonSuggestionModal
          isOpen={isAddonModalOpen}
          onClose={() => setIsAddonModalOpen(false)}
          // CORE-BP item 3: Escape, the backdrop and the X are dismissals too.
          // They reached `onClose` alone, so the entire showing went
          // unrecorded -- the same suggestions-vanish defect as
          // `dismissed_keys: []`, through a different door. It records the
          // outcome and DELIBERATELY nothing else: closing the modal is not a
          // request to create the ticket, so unlike "Create without" it must
          // not continue into OptiPlace or auto-assignment.
          onCloseWithoutChoice={() => recordAddonOutcome(selectedJobs.map(j => j.name))}
          suggestions={pendingAddonSuggestions}
          vehicleInfo={{
            year: form.getValues('vehicle.year'),
            make: form.getValues('vehicle.make'),
            model: form.getValues('vehicle.model'),
          }}
          onAccept={async (acceptedTypes: AddonSuggestionType[]) => {
            console.log('🎯 AddonSuggestionModal onAccept called with types:', acceptedTypes);

            // Helper to resolve which catalog job ID to use for a suggestion type
            const resolveCatalogJobId = async (type: AddonSuggestionType): Promise<string | null> => {
              const existingId = addonSettings?.catalogJobIds[type];
              console.log(`📋 Current mapped catalog job for ${type}:`, existingId);

              if (existingId) return existingId;
              if (!activeShop) return null;

              // Fallback: try to auto-map a sensible default when not configured
              let job = null;
              
              if (type === 'alignment') {
                const { data } = await newJobCatalogQuery('id, label, category_key, sub_menu, minutes_default, skill_level, proficiency_category, requires')
                  .eq('category_key', 'alignment')
                  .ilike('label', '%Four-Wheel Alignment%')
                  .maybeSingle();
                job = data;
                console.log('🧭 Auto-resolved alignment job from catalog:', job);
              } else if (type === 'adas') {
                const { data } = await newJobCatalogQuery('id, label, category_key, sub_menu, minutes_default, skill_level, proficiency_category, requires')
                  .eq('category_key', 'adas')
                  .ilike('label', '%Lane Departure Camera%Calibrate%')
                  .maybeSingle();
                job = data;
                console.log('🎥 Auto-resolved ADAS job from catalog:', job);
              } else if (type === 'sas') {
                const { data } = await newJobCatalogQuery('id, label, category_key, sub_menu, minutes_default, skill_level, proficiency_category, requires')
                  .eq('category_key', 'steering_suspension')
                  .ilike('label', '%Steering Angle Sensor%R&R/Init%')
                  .maybeSingle();
                job = data;
                console.log('🎯 Auto-resolved SAS job from catalog:', job);
              } else if (type === 'tpms') {
                const { data } = await newJobCatalogQuery('id, label, category_key, sub_menu, minutes_default, skill_level, proficiency_category, requires')
                  .eq('category_key', 'tires')
                  .ilike('label', '%TPMS Relearn%')
                  .maybeSingle();
                job = data;
                console.log('🔧 Auto-resolved TPMS job from catalog:', job);
              }

              if (job) {
                // Persist this mapping for future suggestions
                const fieldName = `${type}_catalog_job_id`;
                await supabase.from('addon_suggestion_settings' as any).upsert(
                  {
                    shop_id: activeShop.id,
                    [fieldName]: job.id,
                  },
                  { onConflict: 'shop_id' }
                );

                // Update local settings state so the rest of this session uses it
                setAddonSettings((prev) =>
                  prev
                    ? {
                        ...prev,
                        catalogJobIds: {
                          ...prev.catalogJobIds,
                          [type]: job.id,
                        },
                      }
                    : prev
                );

                console.log(`✅ Successfully mapped and persisted ${type} job:`, job.id);
                return job.id;
              }

              console.log(`⚠️ No catalog job found for ${type} - suggestion will be skipped`);
              return null;
            };
            
            // Add catalog jobs for accepted types
            const newJobs = [];
            for (const type of acceptedTypes) {
              const catalogJobId = await resolveCatalogJobId(type);
              console.log(`📋 Final catalog job ID for ${type}:`, catalogJobId);
              
              if (catalogJobId) {
                const { data: catalogJob } = await supabase
                  .from('jobs_catalog')
                  .select('*')
                  .eq('id', catalogJobId)
                  .single();
                
                console.log(`✅ Loaded catalog job for ${type}:`, catalogJob);
                
                // W2 catalog retirement: a configured id that was retired is LOOKED UP (so we can say
                // why) but never added as new work; the advisor picks the live job instead.
                if (catalogJob && !isOfferableCatalogJob(catalogJob)) {
                  console.warn(`⛔ Configured ${type} catalog job is retired; not added.`, catalogJob.code);
                } else if (catalogJob) {
                  // Core-100 R4 — CORE-BP: the accepted family becomes an ordinary job with the
                  // catalogue's identity and duration (one tested mapping, src/utils/addonAcceptedWork.ts).
                  const newJob = acceptedSuggestionCreationJob(catalogJob as AcceptedCatalogJobRow, crypto.randomUUID());
                  console.log('➕ Adding job:', newJob);
                  newJobs.push(newJob);
                }
              }
            }

            console.log('📦 Total new jobs to add:', newJobs);
            console.log('📦 Current selectedJobs:', selectedJobs);

            // Calculate complete job list with addon suggestions
            const allJobs = [...selectedJobs, ...newJobs];
            console.log('📦 Combined allJobs:', allJobs);
            
            // Add to selectedJobs state
            setSelectedJobs(allJobs);

            // CORE-BP Slice B: one outcome derivation, recorded off the
            // critical path.
            //
            // This previously awaited `getUser()` and then awaited the insert,
            // both BEFORE the advisor could continue -- and then recorded
            // `dismissed_keys: []`, so the suggestions they chose NOT to take
            // simply vanished. Whatever was shown and not accepted is now
            // declined, by the same derivation the other exits use.
            recordAddonOutcome(allJobs.map(j => j.name), acceptedTypes);

            // Update optiPlaceData with ALL jobs including addon suggestions
            const updatedJobs = allJobs.map(job => ({
              ...job,
              tempJobId: (job as any).tempJobId ?? crypto.randomUUID(),
              minutes: Math.round((job.customHours || job.hours) * 60),
            }));
            
            const totalMinutes = updatedJobs.reduce((sum, job) => sum + job.minutes, 0);
            
            console.log('🔄 Updating optiPlaceData with jobs:', updatedJobs);
            
            const updatedOptiPlaceData = {
              ...optiPlaceData,
              jobs: updatedJobs,
              predictedMinutes: totalMinutes,
            };
            
            console.log('📤 Final optiPlaceData:', updatedOptiPlaceData);
            
            setOptiPlaceData(updatedOptiPlaceData);

            // Close addon modal
            setIsAddonModalOpen(false);
            setPendingAddonSuggestions([]);
            
            // CATALYST AUTO-ASSIGN: For Catalyst/Enterprise tiers, bypass OptiPlace modal
            if (isCatalystShop) {
              await performAutoAssign(updatedOptiPlaceData);
              return;
            }
            
            // Core tier - open OptiPlace modal
            setIsOptiPlaceModalOpen(true);
          }}
          onDismiss={async () => {
            // "Create without" -- no accepted set at all, so the shared
            // derivation declines everything shown. That is what dismissing
            // means, and it is recorded off the critical path like the rest.
            recordAddonOutcome(selectedJobs.map(j => j.name));

            // Close addon modal
            setIsAddonModalOpen(false);
            setPendingAddonSuggestions([]);
            
            // CATALYST AUTO-ASSIGN: For Catalyst/Enterprise tiers, bypass OptiPlace modal
            if (isCatalystShop && optiPlaceData) {
              await performAutoAssign(optiPlaceData);
              return;
            }
            
            // Core tier - open OptiPlace modal
            setIsOptiPlaceModalOpen(true);
          }}
          onSuppressType={async (type: AddonSuggestionType) => {
            // Add matched triggers to never_prompt_terms
            const suggestion = pendingAddonSuggestions.find(s => s.type === type);
            if (suggestion && activeShop?.id && addonSettings) {
              const updatedNeverPrompt = [
                ...addonSettings.neverPromptTerms,
                ...suggestion.matchedTriggers
              ];

              await supabase
                .from('addon_suggestion_settings' as any)
                .upsert({
                  shop_id: activeShop.id,
                  never_prompt_terms: updatedNeverPrompt,
                }, { onConflict: 'shop_id' });

              // Update local settings
              setAddonSettings({
                ...addonSettings,
                neverPromptTerms: updatedNeverPrompt
              });

              toast({ title: `Suppressed "${type}" suggestions for these job types` });
            }
          }}
        />
      )}

      {optiPlaceData && (
        <OptiPlaceModal
          isOpen={isOptiPlaceModalOpen}
          onClose={() => {
            setIsOptiPlaceModalOpen(false);
            setOptiPlaceData(null);
          }}
          ticketData={optiPlaceData}
          onCallProposalRefused={(message) => setCallProposal((prev) => prev && ({ ...prev, state: 'refused', message }))}
          onAssignmentComplete={async (ticketId) => {
            // V-OCIS-Intake-RawAudio-Batch-v1: best-effort attach before navigate.
            await attachPendingRecordingToTicket(ticketId);
            navigate(`/?tab=dayboard&highlight=${ticketId}`);
          }}
        />
      )}

      {/* Edit Single Job Dialog */}
      {editingJobIndex !== null && selectedJobs[editingJobIndex] && (
        <EditSingleJobDialog
          isOpen={editingJobIndex !== null}
          onClose={() => setEditingJobIndex(null)}
          jobName={selectedJobs[editingJobIndex].name}
          currentHours={selectedJobs[editingJobIndex].customHours || selectedJobs[editingJobIndex].hours}
          onConfirm={(newHours) => {
            setSelectedJobs(prev => prev.map((job, i) => 
              i === editingJobIndex 
                ? { ...job, hours: newHours, customHours: newHours }
                : job
            ));
            setEditingJobIndex(null);
          }}
        />
      )}
      
      {/* VIN Barcode Scanner Dialog */}
      <VinScannerDialog
        open={vinScannerOpen}
        onOpenChange={setVinScannerOpen}
        onVinDetected={handleVinDetected}
      />

      {/* AI Intake Assist (paste transcript / record audio) */}
      {aiIntakeEnabled && (
        <AiIntakeAssistDialog
          isOpen={aiIntakeOpen}
          onClose={() => setAiIntakeOpen(false)}
          shopId={activeShop?.id ?? null}
          onApply={handleAiIntakeApply}
        />
      )}

      {/* Upload Repair Order modal — same component as the previous
          inline section, just wrapped in a Dialog opened from the top
          button cluster. onDataExtracted closes the modal once the
          form has been populated so the advisor lands back on the
          ticket form with the fields filled. */}
      <Dialog open={isROUploadOpen} onOpenChange={setIsROUploadOpen}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="upload-ro-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload Repair Order
            </DialogTitle>
            <DialogDescription>
              Upload a repair-order PDF or image and OptiBay will extract
              the vehicle, customer, and job details into this form.
            </DialogDescription>
          </DialogHeader>
          <div data-testid="ro-upload-section">
            <ROUpload
              shopId={activeShop?.id ?? null}
              onDataExtracted={async (data) => {
                console.log('[ROUpload] Parsed data from parse-ro:', data);
                setIsPopulatingFromRO(true);
                try {
                  if (data.year && (data.make || data.model)) {
                    await populateVehicleFromParsed(data.year, data.make, data.model);
                  } else if (data.year) {
                    form.setValue('vehicle.year', data.year);
                    setSelectedYear(data.year);
                  }
                  if (data.engineSize) {
                    form.setValue('vehicle.engineSize', data.engineSize);
                  }
                  if (data.vin) {
                    form.setValue('vehicle.vin', data.vin);
                    await decodeVin(data.vin);
                  }
                  if (data.licensePlate) {
                    form.setValue('vehicle.licensePlate', data.licensePlate);
                  }
                  if (data.customerName) {
                    form.setValue('customerName', data.customerName);
                  }
                  if (data.customerPhone) {
                    form.setValue('customerPhone', data.customerPhone);
                  }
                  // Core-100 R4 CORE-Y: the odometer the repair order states is proposed too (it was extracted and
                  // then dropped), and marked as extracted like every other field.
                  const roMiles = typeof data.mileage === 'number' ? data.mileage : parseInt(String(data.mileage ?? '').replace(/[,\s]/g, ''), 10);
                  const roMilesOk = Number.isFinite(roMiles) && roMiles > 0 && roMiles < 1_000_000;
                  if (roMilesOk) {
                    form.setValue('vehicle.odometerMiles', roMiles);
                  }
                  setExtractionProvenance(roExtractionProvenance({ ...data, odometerMiles: roMilesOk ? roMiles : null }));
                  if (data.jobs && Array.isArray(data.jobs)) {
                    setSelectedJobs([
                      ...selectedJobs,
                      ...data.jobs.map((j: any) => ({
                        ...j,
                        tempJobId: j.tempJobId ?? crypto.randomUUID(),
                      })),
                    ]);
                  }
                  // Review context: complaints and unsold recommendations are
                  // recorded for display only. They are deliberately NOT merged
                  // into selectedJobs, the customer name or a notes field.
                  const hasReviewContext =
                    (data.customer_comments?.length ?? 0) > 0 ||
                    (data.recommendations?.length ?? 0) > 0 ||
                    (data.unmatched_jobs?.length ?? 0) > 0;
                  setRoReviewContext(
                    hasReviewContext
                      ? {
                          sourceFormat: data.source_format,
                          customerComments: data.customer_comments,
                          recommendations: data.recommendations,
                          unmatchedJobs: data.unmatched_jobs,
                          serviceWriter: data.service_writer,
                          createdDate: data.created_date,
                          ticketNumber: data.ticketNumber,
                        }
                      : null,
                  );
                  // Close the modal once the form has been populated so
                  // the advisor lands back on the ticket form.
                  setIsROUploadOpen(false);
                } finally {
                  setIsPopulatingFromRO(false);
                }
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default CreateTicket;
